import React, { useEffect, useState, useRef, useMemo } from 'react'
import {
  Tag,
  Button,
  message,
  Pagination,
  Drawer,
  Empty,
  Typography,
  Spin,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Space,
  Modal,
  Card,
  Divider,
  Dropdown,
  Checkbox
} from 'antd'
import { DatePicker as MobileDatePicker } from 'antd-mobile'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { usePermission } from '../hooks/usePermission'
import Hint from './common/Hint'
import { fetchPendingAssignments } from '../api/booking'
import AssignStylistModal from './booking/AssignStylistModal'
import {
  SearchOutlined,
  PlusOutlined,
  DeleteOutlined,
  DollarOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  DownOutlined,
  MoreOutlined,
  EditOutlined,
  CalendarOutlined,
  RightOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import api from '../utils/api'
import moment from 'moment'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { now as tzNow } from '../utils/timezone'
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom'
import {
  readBookingRangePreset,
  writeBookingRangePreset,
  bookingRangeOfPreset,
  presetOfBookingRange,
  formatBookingRangeLabel
} from '../utils/bookingRangePreset'
import {
  getCustomerDisplayData,
  resolvePlatformUser
} from '../utils/customerDisplay'

dayjs.extend(isoWeek)

const { Option } = Select


const ResponsiveTable = styled.div`
  background-color: #f8f9fa;
  border-radius: 12px;
  min-height: 400px;

  .empty-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
    color: #86868b;
    font-size: 16px;
  }

  .cards-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 20px;
    min-width: 0;
    width: 100%;
  }

  @media (max-width: 768px) {
    .cards-container {
      gap: 8px;
    }
  }
`

const BookingCard = styled.div`
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e5e7;
  padding: 16px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  cursor: pointer;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;

  /* 桌機：固定欄寬，與上一筆卡片垂直對齊 */
  @media (min-width: 769px) {
    display: grid;
    grid-template-columns:
      minmax(0, 1.35fr)
      104px
      76px
      minmax(0, 1fr)
      minmax(0, 0.8fr)
      92px
      minmax(0, auto);
    column-gap: 16px;
    row-gap: 4px;
    align-items: center;
  }

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-color: #d5728a;
  }

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    column-gap: 10px;
    row-gap: 10px;
    align-items: center;
    padding: 12px 16px;
    overflow-x: hidden;
  }
`

/* 桌機：子元素直接參與父層 grid；手機：自成一排 */
const CardDateTimeRow = styled.div`
  display: contents;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
    grid-column: 1;
    grid-row: 3;
    width: 100%;
    min-width: 0;
    margin-top: 0;
    padding-top: 10px;
    padding-bottom: 2px;
    border-top: 1px solid #f0f0f0;
    align-self: stretch;
  }
`

/* 手機：單行日期＋時間；桌機不顯示 */
const BookingMobileDateSummary = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-weight: 500;
    color: #86868b;
    line-height: 1.3;

    .anticon {
      color: #d5728a;
      font-size: 14px;
      flex-shrink: 0;
    }
  }
`

const BookingCardChevronMobile = styled(RightOutlined)`
  display: none;

  @media (max-width: 768px) {
    display: block;
    flex-shrink: 0;
    color: #d5728a;
    font-size: 13px;
    opacity: 0.85;
    margin-left: 2px;
  }
`

/* 桌機參與 grid；手機隱藏（改由底部單行日期＋箭頭） */
const DesktopOnlyDateTimeBlock = styled.div`
  display: contents;

  @media (max-width: 768px) {
    display: none;
  }
`

const CardMetaRow = styled.div`
  display: contents;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
    align-items: start;
    column-gap: 10px;
    row-gap: 4px;
    width: 100%;
    min-width: 0;
    grid-column: 1 / -1;
    grid-row: 2;
  }
`

const CardColService = styled.div`
  min-width: 0;

  @media (max-width: 768px) {
    width: 100%;
    grid-column: 1 / -1;
    grid-row: 1;
  }
`

const CardServiceTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1d1d1f;
  line-height: 1.35;
  word-break: break-word;
  overflow-wrap: anywhere;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`

const CardSubtitle = styled.div`
  font-size: 11px;
  color: #6c757d;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 4px;

  @media (max-width: 768px) {
    font-size: 11px;
    margin-top: 4px;
  }
`

const CardColActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
  min-width: 0;

  @media (min-width: 769px) {
    justify-self: end;
    justify-content: flex-end;
  }

  @media (max-width: 768px) {
    grid-column: 2;
    grid-row: 3;
    width: max-content;
    max-width: 100%;
    justify-content: flex-end;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    row-gap: 6px;
    padding-top: 10px;
    padding-bottom: 2px;
    border-top: 1px solid #f0f0f0;
    align-self: stretch;
    justify-self: end;
  }
`

const DeleteButtonLabel = styled.span`
  @media (max-width: 768px) {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  background: ${(props) => props.$color || '#f5f5f7'};
  color: ${(props) => props.$textColor || '#1d1d1f'};
  border: 1px solid rgba(0, 0, 0, 0.05);
  ${(props) =>
    props.$clickable
      ? `
    cursor: pointer;
    transition: all 0.2s ease;
    &:hover {
      opacity: 0.8;
      transform: scale(1.05);
    }
  `
      : ''}

  @media (max-width: 768px) {
    padding: 3px 8px;
    font-size: 11px;
    border-radius: 10px;
    flex-shrink: 0;
  }
`

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  @media (min-width: 769px) {
    min-width: 0;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2px;
    padding: 0;
    border: none;
    max-width: 100%;
  }
`

const InfoLabel = styled.span`
  font-size: 10px;
  color: #6c757d;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`

const InfoValue = styled.span`
  font-size: 13px;
  color: #1d1d1f;
  font-weight: 500;
  word-break: break-word;
  overflow-wrap: anywhere;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`

const PriceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  @media (max-width: 768px) {
    flex-shrink: 0;
    text-align: right;
    justify-self: end;
    min-width: min-content;
  }
`

const PriceLabel = styled.span`
  font-size: 10px;
  color: #6c757d;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;

  @media (max-width: 768px) {
    font-size: 10px;
  }
`

const PriceValue = styled.span`
  font-size: 14px;
  color: #1d1d1f;
  font-weight: 600;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: wrap;
  align-items: center;
  min-width: 0;

  @media (max-width: 768px) {
    gap: 4px;
    max-width: 100%;
    justify-content: flex-end;
    flex-wrap: wrap;
  }
`

const ActionButton = styled(Button)`
  height: 28px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid #e5e5e7;
  background: #fff;
  color: #1d1d1f;
  transition: all 0.2s ease;

  &:hover {
    border-color: #d5728a;
    color: #d5728a;
    background: #fff;
  }

  &.primary {
    background: #d5728a;
    border-color: #d5728a;
    color: #fff;

    &:hover {
      background: #c4596f;
      border-color: #c4596f;
      color: #fff;
    }
  }

  &.danger {
    border-color: #ff3b30;
    color: #ff3b30;

    &:hover {
      background: #ff3b30;
      color: #fff;
    }
  }

  @media (max-width: 768px) {
    height: 25px;
    font-size: 11px;
    padding: 0 5px;
    min-width: auto;
    flex-shrink: 0;
  }

  &.booking-delete-btn-mobile {
    @media (max-width: 768px) {
      padding: 0;
      width: 25px;
      min-width: 25px;
      height: 25px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
  }
`

const TableStyles = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding-bottom: 16px;
  .total-count {
    color: #6c757d;
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  th {
    background-color: #f5f5f7;
    padding: 16px 20px;
    font-weight: 600;
    color: #1d1d1f;
    border-bottom: 1px solid #e5e5e7;
    text-align: left;
    font-size: 14px;
    letter-spacing: -0.01em;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  td {
    padding: 16px 20px;
    border-bottom: 1px solid #f5f5f7;
    color: #1d1d1f;
    font-size: 14px;
    vertical-align: middle;
  }

  tr {
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #f5f5f7;
    }

    &:last-child td {
      border-bottom: none;
    }
  }

  .pagination-wrapper {
    margin-top: 0;
    padding: 16px 0;
    border-top: none;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    background-color: transparent;

    .total-count {
      color: #6c757d;
      font-size: 12px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    .ant-pagination {
      align-self: center;

      .ant-pagination-item {
        border: 1px solid #e5e5e7;
        border-radius: 8px;

        &:hover {
          border-color: #d5728a;
        }

        &.ant-pagination-item-active {
          background-color: #d5728a;
          border-color: #d5728a;
        }
      }
    }
  }

  @media (max-width: 820px) {
    border-radius: 0;
    border-left: none;
    border-right: none;

    .desktop-only {
      display: none;
    }

    th,
    td {
      padding: 12px 16px;
      font-size: 14px;
    }
  }
`

const { Title, Text } = Typography

const StyledDrawerContent = styled.div`
  .ant-descriptions-item-label {
    font-weight: bold;
    color: #1890ff;
  }

  .ant-descriptions-item-content {
    text-align: right;
  }

  .edit-field {
    width: 100%;
    margin-top: 8px;
  }

  .status-tag {
    margin-top: 8px;
  }

  .note-area {
    width: 100%;
    margin-top: 8px;
  }
`

const StyledTag = styled(Tag)`
  border-radius: 20px;
  padding: 4px 12px;
  font-weight: 500;
  font-size: 12px;
  border: none;
  color: #fff;

  &.ant-tag-green {
    background: #34c759;
  }

  &.ant-tag-blue {
    background: #d5728a;
  }

  &.ant-tag-red {
    background: #ff3b30;
  }

  &.ant-tag-orange {
    background: #ff9500;
  }

  &.ant-tag-purple {
    background: #af52de;
  }

  &.ant-tag-gray {
    background: #8e8e93;
  }
`

const FilterSection = styled.div`
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`

const SearchSection = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  margin-bottom: 20px;
  padding: 8px 16px;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 8px 0px;
  }

  .search-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: nowrap;
    padding-bottom: 4px;

    @media (max-width: 768px) {
      padding-left: 10px;
      padding-right: 10px;
      flex-wrap: wrap;
      gap: 8px;
      overflow-x: visible;
      padding-bottom: 4px;
    }
  }

  /* 目前套用的日期區間提示 */
  .range-hint {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 2px;
    margin-top: 8px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);

    strong {
      color: #8a6430;
      font-weight: 600;
    }

    .ant-btn-link {
      height: auto;
      padding: 0 4px;
      font-size: 12px;
    }

    @media (max-width: 768px) {
      padding-left: 10px;
      padding-right: 10px;
    }
  }

  /* 桌機：不參與排版；手機：兩列撐滿寬（搜尋列 3:2:1:1、設計師+進階 1:1） */
  .search-toolbar-row {
    display: contents;

    @media (max-width: 768px) {
      display: flex;
      flex: 1 1 100%;
      flex-direction: column;
      align-items: stretch;
      flex-wrap: nowrap;
      justify-content: flex-start;
      gap: 8px;
      width: 100%;
      min-width: 0;
    }
  }

  /* 搜索、重置、今日、本週 — 手機 grid 3:2:1:1；桌機橫排 */
  .search-quick-row {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    background: #fff;
    border-radius: 8px;
    padding: 2px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    @media (max-width: 768px) {
      display: grid;
      grid-template-columns: 3fr 2fr 1fr 1fr;
      gap: 6px;
      width: 100%;
      background: transparent;
      box-shadow: none;
      padding: 0;
    }

    .compact-btn,
    .reset-btn,
    .quick-range-btn {
      @media (max-width: 768px) {
        width: 100%;
        min-width: 0;
        height: 40px !important;
        padding-left: 4px;
        padding-right: 4px;
      }
    }

    .quick-range-btn {
      height: 35px !important;
      font-size: 12px;
      border-radius: 8px;
      padding: 0 6px;

      @media (max-width: 768px) {
        height: 40px !important;
      }
    }
  }

  .search-input {
    flex: 2;
    min-width: 200px;
    max-width: 300px;

    @media (max-width: 768px) {
      flex: 1 1 100%;
      width: 100%;
      min-width: 100%;
      max-width: 100%;
    }
  }

  .filter-group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;

    @media (max-width: 768px) {
      display: none;
    }
  }

  .filter-item {
    min-width: 120px;
    max-width: 150px;
  }

  .action-buttons {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    margin-left: auto;

    @media (max-width: 768px) {
      margin-left: 0;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px 8px; /* 行距 10px、欄距 8px：排序列與服務人員列間留出間隔 */
      width: 100%;
      align-items: stretch;
      margin-top: 4px; /* 與上方搜尋/快捷列多留一點間隔 */
    }

    /* 手機：服務人員下拉與進階同高（40px）、欄寬 1:1 */
    .booking-stylist-select {
      @media (max-width: 768px) {
        width: 100% !important;
        min-width: 0 !important;
        max-width: none !important;
      }
    }

    /* 手機：日期/時間排序下拉撐滿格寬、與其他控件同高 */
    .booking-sort-select {
      @media (max-width: 768px) {
        width: 100% !important;
        min-width: 0 !important;
        max-width: none !important;
      }
    }

    /* 桌機：與右側「服務人員」下拉同高（同樣的 selector padding） */
    .booking-sort-select .ant-select-selector {
      padding: 17px 11px !important;
      display: flex;
      align-items: center;

      @media (max-width: 768px) {
        padding: 0 11px !important;
        font-size: 12px;
        height: 40px !important;
        min-height: 40px !important;
      }
    }

    .booking-stylist-select .ant-select-selector {
     padding: 17px 11px !important;
      @media (max-width: 768px) {
        font-size: 12px;
        height: 40px !important;
        min-height: 40px !important;
        display: flex !important;
        align-items: center !important;
      }
    }

    .booking-stylist-select.ant-select-single
      .ant-select-selector
      .ant-select-selection-item,
    .booking-stylist-select.ant-select-single
      .ant-select-selector
      .ant-select-selection-placeholder {
      @media (max-width: 768px) {
        line-height: 38px !important;
      }
    }

    .booking-stylist-select .ant-select-selection-search-input {
      @media (max-width: 768px) {
        height: 38px !important;
      }
    }
  }


  .mobile-advanced-btn {
    display: none;

    @media (max-width: 768px) {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      width: 100%;
      min-width: 0;
      background: #fff;
      border: none;
      border-radius: 8px;
      padding: 6px 10px;
      font-size: 12px;
      color: #666;
      box-shadow: none;
      height: 40px !important;
    }
  }

  .ant-input,
  .ant-select-selector,
  .ant-picker {
    background: #fff;
    border: none;
    border-radius: 8px;
    height: 30px !important;
    box-shadow: none !important;
    transition: all 0.2s ease;

    &:hover {
      box-shadow: none !important;
    }

    &:focus,
    &.ant-picker-focused {
      box-shadow: 0 0 0 3px rgba(213, 114, 138, 0.2) !important;
    }
  }

  .ant-input {
    padding: 0 12px;
    font-size: 14px;
  }

  .ant-select-selector {
    padding: 0 12px;
  }

  .ant-picker {
    padding: 0 12px;
  }

  .ant-btn {
    height: 40px !important;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    border: none;
    box-shadow: none !important;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: auto;
    padding: 0 12px;

    &:hover {
      box-shadow: none !important;
    }

    &.ant-btn-primary {
      background: #d5728a;
      border-color: #d5728a;

      &:hover {
        background: #c4657a;
        border-color: #c4657a;
      }
    }

    &.ant-btn-default {
      background: #fff;
      color: #666;

      &:hover {
        background: #f5f5f5;
        color: #333;
      }
    }

    &.compact-btn {
      width: 100px;
      height: 35px !important;
      padding: 0 8px;
      font-size: 12px;
      border-radius: 6px;
      @media (max-width: 768px) {
        width: 60px;
      }
    }
    &.reset-btn {
      width: 80px;
      height: 35px !important;
      padding: 0 8px;
      font-size: 12px;
      border-radius: 6px;
      @media (max-width: 768px) {
        width: 50px;
      }
    }
  }
`

const AdvancedSearchDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 20px;
  }

  .drawer-content {
    .filter-section {
      margin-bottom: 24px;

      .section-title {
        font-size: 16px;
        font-weight: 600;
        color: #1d1d1f;
        margin-bottom: 12px;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .filter-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 16px;
      }
    }

    @media (min-width: 769px) {
      .filter-section {
        margin-bottom: 20px;

        .section-title {
          font-size: 14px;
          margin-bottom: 10px;
        }

        .filter-grid {
          gap: 12px;
        }
      }
    }

    .ant-input,
    .ant-select-selector,
    .ant-picker {
      background: #f8f9fa;
      border: none;
      border-radius: 8px;
      height: 44px !important;
      box-shadow: none !important;
      transition: all 0.2s ease;

      &:hover {
        background: #f0f0f0;
        box-shadow: none !important;
      }

      &:focus,
      &.ant-picker-focused {
        background: #fff;
        box-shadow: 0 0 0 3px rgba(213, 114, 138, 0.2) !important;
      }
    }

    @media (min-width: 769px) {
      .ant-input,
      .ant-select-selector,
      .ant-picker {
        height: 30px !important;
      }
    }

    .ant-input {
      padding: 0 16px;
      font-size: 16px;
    }

    .ant-select-selector {
      padding: 0 16px;
    }

    .ant-picker {
      padding: 0 16px;
    }

    .ant-btn {
      height: 44px;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 500;
      border: none;
      box-shadow: none !important;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        box-shadow: none !important;
      }

      &.ant-btn-primary {
        background: #d5728a;
        border-color: #d5728a;

        &:hover {
          background: #c4657a;
          border-color: #c4657a;
        }
      }

      &.ant-btn-default {
        background: #f8f9fa;
        color: #666;

        &:hover {
          background: #f0f0f0;
          color: #333;
        }
      }
    }

    @media (min-width: 769px) {
      .ant-btn {
        height: 30px !important;
        font-size: 14px;
      }
    }
  }
`

const PageHeader = styled.div`
  background: #fff;
  padding: 32px;
  border-radius: 12px;
  border: 1px solid #e5e5e7;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .left-section {
    .title {
      margin: 0;
      color: #1d1d1f;
      font-size: 28px;
      font-weight: 600;
      letter-spacing: -0.02em;
    }

    .subtitle {
      color: #86868b;
      margin-top: 8px;
      font-size: 16px;
      line-height: 1.4;
    }
  }

  .right-section {
    display: flex;
    gap: 24px;
    align-items: center;
  }

  .stats-section {
    display: flex;
    gap: 32px;
  }

  .stats-item {
    text-align: center;

    .stats-label {
      color: #86868b;
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 4px;
    }

    .stats-value {
      color: #1d1d1f;
      font-size: 24px;
      font-weight: 600;
      line-height: 1;
    }
  }

  .add-button {
    background: #d5728a;
    border: none;
    padding: 12px 24px;
    height: 44px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s ease;
    font-weight: 500;

    &:hover {
      background: #c4596f;
      transform: translateY(-1px);
    }

    .anticon {
      font-size: 16px;
    }
  }

  @media (max-width: 820px) {
    flex-direction: column;
    gap: 20px;
    padding: 24px 20px;

    .left-section {
      width: 100%;
      text-align: center;
    }

    .right-section {
      width: 100%;
      flex-direction: column;
      gap: 16px;
    }

    .stats-section {
      display: none;
    }

    .add-button-wrapper {
      width: 100%;
    }
  }
`

const BankNumber = styled.span`
  color: #666;
  font-size: 13px;
  font-family: monospace;
  letter-spacing: 0.5px;
  margin-left: 8px;
`

const DepositWrapper = styled.div`
  display: flex;
  align-items: start;
  flex-direction: column;
  gap: 8px;
`
const DepositDrawerWrapper = styled.div`
  display: flex;
  align-items: end;
  justify-content: flex-end;
  gap: 8px;
`

const SwipeableRow = styled.tr`
  position: relative;
  transition: all 0.3s ease;
  width: 100%;
  display: table-row;
  cursor: pointer;

  td {
    position: relative;
    z-index: 2;
    background: inherit;
  }

  .delete-icon {
    color: #ff4d4f;
    cursor: pointer;
    font-size: 18px;

    &:hover {
      color: #cf1322;
    }
  }
`

const DesktopPicker = styled.div`
  display: none;
  @media (min-width: 769px) {
    display: block;
  }
`

const MobilePicker = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
  }
  @media (min-width: 769px) {
    display: none;
  }
`

const SortIcon = styled.span`
  margin-left: 8px;
  color: ${(props) => (props.active ? '#1890ff' : '#bfbfbf')};
  cursor: pointer;

  &:hover {
    color: #1890ff;
  }
`

/** 定金收款方式的顯示文字（與後端 Booking.depositPayment.method 的 enum 對應）*/
const DEPOSIT_METHOD_LABELS = {
  credit: '刷卡',
  cash: '現金',
  bank_transfer: '匯款'
}

const BookingCardComponent = ({
  booking,
  onDelete,
  onShowModal,
  onCancelOrder,
  onRecheckout,
  onUpdateDate,
  customerDisplayNameOverride
}) => {
  const navigate = useNavigate()
  const { hasFeature } = useMembership() // 會員方案權限檢查

  // 從預約數據中直接讀取結帳狀態（方案三）
  const hasOrder = booking?.hasCheckedOut === true
  const orderInfo = booking?.orderId
    ? {
        orderId: booking.orderId,
        status: booking.orderStatus || 'paid'
      }
    : null

  const handleCardClick = (e) => {
    // 如果點擊的是按鈕，不觸發卡片點擊
    if (e.target.closest('button')) {
      return
    }
    onShowModal(booking)
  }

  const handleCheckout = (e) => {
    e.stopPropagation()
    if (hasOrder) {
      message.warning('此預約已結帳，請使用「取消交易並再次結帳」功能')
      return
    }
    navigate(`/checkout/checkout?bookingId=${booking._id}`)
  }

  const handleCancelOrder = async (e) => {
    e.stopPropagation()
    if (!orderInfo?.orderId) return

    Modal.confirm({
      title: '確認取消交易',
      content: `確定要取消訂單 ${orderInfo.orderId} 的交易嗎？此操作將作廢該訂單。`,
      okText: '確認',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await onCancelOrder(orderInfo.orderId, booking._id)
          // 父組件會重新加載預約列表，狀態會自動更新
          message.success('交易已取消')
        } catch (error) {
          message.error('取消交易失敗')
        }
      }
    })
  }

  const handleRecheckout = (e) => {
    e.stopPropagation()
    if (!orderInfo?.orderId) return

    Modal.confirm({
      title: '重新結帳',
      content:
        '此操作將取消原訂單並重新結帳，原訂單的消費內容（服務、商品、客戶、付款方式等）將自動帶入結帳頁面。確定繼續嗎？',
      okText: '確認',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await onCancelOrder(orderInfo.orderId, booking._id)
          const params = new URLSearchParams({ recheckout: 'true' })
          params.set('orderId', orderInfo.orderId)
          params.set('bookingId', booking._id)
          navigate(`/checkout/checkout?${params.toString()}`)
        } catch (error) {
          message.error('操作失敗')
        }
      }
    })
  }

  const getStatusConfig = (status) => {
    const configs = {
      confirmed: { color: '#d4edda', textColor: '#155724', text: '已確認' },
      pending: { color: '#fff3cd', textColor: '#856404', text: '待確認' },
      canceled: { color: '#f8d7da', textColor: '#721c24', text: '已取消' },
      completed: { color: '#d1ecf1', textColor: '#0c5460', text: '已完成' },
      no_show: { color: '#e2e3e5', textColor: '#383d41', text: '未到' }
    }
    return configs[status] || configs.pending
  }

  const getBookingDisplayName = (b) => {
    if (b?.services?.length) {
      const names = b.services.map((s) => s.serviceName).filter(Boolean)
      if (names.length) return names.join(' + ')
    }
    if (b?.productName) return b.productName
    return '（未註明服務）'
  }

  const statusConfig = getStatusConfig(booking.status)

  return (
    <BookingCard onClick={handleCardClick}>
      <CardColService>
        <CardServiceTitle>{getBookingDisplayName(booking)}</CardServiceTitle>
      </CardColService>

      <CardDateTimeRow>
        <DesktopOnlyDateTimeBlock>
          <InfoItem>
            <InfoLabel>日期</InfoLabel>
            <InfoValue>{dayjs(booking.date).format('YYYY/MM/DD')}</InfoValue>
          </InfoItem>
        </DesktopOnlyDateTimeBlock>
        <DesktopOnlyDateTimeBlock>
          <InfoItem>
            <InfoLabel>時間</InfoLabel>
            <InfoValue>{booking.time}</InfoValue>
          </InfoItem>
        </DesktopOnlyDateTimeBlock>
        <BookingMobileDateSummary>
          {/* <CalendarOutlined aria-hidden /> */}
          <span>
            {dayjs(booking.date).format('YYYY-MM-DD')} {booking.time}
          </span>
        </BookingMobileDateSummary>
      </CardDateTimeRow>

      <CardMetaRow>
        <InfoItem>
          <InfoLabel>客戶</InfoLabel>
          <InfoValue>
            {customerDisplayNameOverride ?? booking.userName}
          </InfoValue>
        </InfoItem>
        <InfoItem>
          <InfoLabel>服務人員</InfoLabel>
          <InfoValue>
            {booking.assignmentStatus === 'pending' ? (
              // 待指派要跟「舊資料剛好沒填」分得開：前者店家要動手，後者不用
              <span style={{ color: '#c98a3d', fontWeight: 600 }}>待指派</span>
            ) : (
              booking.stylistName || '未指定'
            )}
          </InfoValue>
        </InfoItem>
        <PriceInfo>
          <PriceLabel>總金額</PriceLabel>
          <PriceValue>
            ${booking.discountedPrice || booking.totalOriginalPrice || 0}
          </PriceValue>
        </PriceInfo>
      </CardMetaRow>

      <CardColActions>
        <StatusBadge $color={statusConfig.color} $textColor={statusConfig.textColor}>
          {statusConfig.text}
        </StatusBadge>
        {/*
          已收的定金與收款方式。
          「已付定金」四個字看不出這筆錢是刷卡還是現金 —— 刷卡要扣手續費、
          現金直接進抽屜，對帳時是兩回事，所以把方式一起標出來。
          刷卡用實心一點的底色，掃一眼就找得到需要對收單行帳的那幾筆。
        */}
        {booking.depositStatus === 'paid' && (
          <StatusBadge
            $color={
              booking.depositPayment?.method === 'credit' ? '#e8dff5' : '#f0f0f2'
            }
            $textColor={
              booking.depositPayment?.method === 'credit' ? '#5b3f86' : '#555'
            }
            title={
              booking.depositPayment?.method === 'credit' &&
              booking.depositPayment?.fee > 0
                ? `刷卡手續費 $${booking.depositPayment.fee}（費率 ${booking.depositPayment.feeRate || 0}%）`
                : undefined
            }
          >
            定金
            {booking.depositAmount > 0 ? ` $${booking.depositAmount}` : ''}
            {DEPOSIT_METHOD_LABELS[booking.depositPayment?.method]
              ? `・${DEPOSIT_METHOD_LABELS[booking.depositPayment.method]}`
              : ''}
          </StatusBadge>
        )}
        {hasOrder && hasFeature('checkout') ? (
          <Dropdown
            menu={{
              items: [
                {
                  key: 'updateDate',
                  label: '改日期',
                  icon: <EditOutlined />,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation()
                    if (onUpdateDate) {
                      onUpdateDate(booking)
                    }
                  }
                },
                {
                  key: 'cancel',
                  label: '取消交易',
                  icon: <CloseCircleOutlined />,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation()
                    handleCancelOrder(domEvent)
                  }
                },
                {
                  key: 'recheckout',
                  label: '重新結帳',
                  icon: <ReloadOutlined />,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation()
                    handleRecheckout(domEvent)
                  }
                }
              ]
            }}
            trigger={['click']}
            placement='bottomRight'
            getPopupContainer={(triggerNode) =>
              triggerNode.parentNode || document.body
            }
          >
            <StatusBadge
              $color='#d4edda'
              $textColor='#155724'
              $clickable
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation()
              }}
            >
              {/* <CheckCircleOutlined style={{ marginRight: 4 }} /> */}
              已結帳
              <DownOutlined style={{ marginLeft: 4, fontSize: 10 }} />
            </StatusBadge>
          </Dropdown>
        ) : hasOrder ? (
          <StatusBadge $color='#d4edda' $textColor='#155724'>
            {/* <CheckCircleOutlined style={{ marginRight: 4 }} /> */}
            已結帳
          </StatusBadge>
        ) : null}
        <ActionButtons>
          {hasFeature('checkout') &&
            !hasOrder &&
            booking?.status !== 'canceled' && (
              <ActionButton
                size='small'
                type='primary'
                icon={<DollarOutlined />}
                onClick={handleCheckout}
                style={{
                  background: '#52c41a',
                  borderColor: '#52c41a',
                  color: '#fff'
                }}
              >
                結帳
              </ActionButton>
            )}
          <ActionButton
            size='small'
            danger
            className='booking-delete-btn-mobile'
            icon={<DeleteOutlined />}
            title='刪除預約'
            aria-label='刪除預約'
            onClick={(e) => {
              e.stopPropagation()
              Modal.confirm({
                title: '確認刪除',
                content: '確定要刪除這個預約嗎？',
                okText: '確認',
                cancelText: '取消',
                onOk: () => onDelete(booking._id)
              })
            }}
          >
            <DeleteButtonLabel>刪除</DeleteButtonLabel>
          </ActionButton>
        </ActionButtons>
        <BookingCardChevronMobile aria-hidden />
      </CardColActions>
    </BookingCard>
  )
}

const BookingTable = ({
  bookings: initialBookings,
  loading: initialLoading,
  isCustomerView,
  onPageChange,
  customerInfo,
  user
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()

  // 從 URL 獲取初始值
  const initialPage = parseInt(searchParams.get('page')) || 1
  const initialKeyword = searchParams.get('keyword') || ''
  const initialStatus = searchParams.get('status') || null
  const initialDepositStatus = searchParams.get('depositStatus') || null
  const initialCheckoutStatus = searchParams.get('checkoutStatus') || null
  const initialFilterStylistId = searchParams.get('stylistId') || null
  const initialDateRange = searchParams.get('dateRange')
    ? [
        moment(searchParams.get('startDate')),
        moment(searchParams.get('endDate'))
      ]
    : null

  // 判斷 URL 是否包含搜索參數
  const hasSearchParams =
    initialKeyword ||
    initialStatus ||
    initialDepositStatus ||
    initialCheckoutStatus ||
    initialDateRange ||
    initialFilterStylistId

  const [currentPage, setCurrentPage] = useState(initialPage)
  const [keyword, setKeyword] = useState(initialKeyword)
  const [selectedStatus, setSelectedStatus] = useState(initialStatus)
  const [selectedDepositStatus, setSelectedDepositStatus] = useState(initialDepositStatus)
  // 結帳狀態：checked_out 已結帳 / not_checked_out 未結帳
  const [selectedCheckoutStatus, setSelectedCheckoutStatus] = useState(initialCheckoutStatus)
  const [dateRange, setDateRange] = useState(initialDateRange)
  const [filterStylistId, setFilterStylistId] = useState(initialFilterStylistId)
  const [stylistFilterOptions, setStylistFilterOptions] = useState([])
  const [isSearching, setIsSearching] = useState(hasSearchParams) // 根據 URL 參數設置初始搜索狀態

  /**
   * 進站預設區間（只在這次進站、且網址完全沒帶參數時套用一次）。
   *
   * 店家反應每次進預約管理都要再按一次「今日」，所以記住他上次選的，
   * 沒有記錄時預設「今日」。記憶值是 'all' 就維持原本的全部預約。
   *
   * 只在網址乾淨時才套用 —— 從客戶詳情、待指派清單、通知等地方帶著
   * keyword／stylistId／日期進來時，那些條件才是店家當下要看的東西，
   * 不能被這個偏好蓋掉。用 ref 而不是 state：它只是「這次進站要做的一件事」，
   * 做完就清掉，不該再觸發任何 render。
   */
  const pendingPresetRef = useRef(
    (() => {
      if (isCustomerView || searchParams.toString()) return null
      const preset = readBookingRangePreset() || 'today'
      return preset === 'today' || preset === 'week' ? preset : null
    })()
  )
  const { businessId } = useAuth()
  // 店家是否啟用定金功能。未啟用時整個定金狀態欄位不顯示，
  // 直接沿用 initialValues 的 not_required（讀取失敗也一律視為未啟用）
  const [depositEnabled, setDepositEnabled] = useState(false)
  useEffect(() => {
    if (!businessId) return
    api
      .get(`/api/business_B/${businessId}/deposit-settings`)
      .then((res) => setDepositEnabled(res?.data?.data?.isEnabled === true))
      .catch(() => setDepositEnabled(false))
  }, [businessId])

  const updateUrlParams = (params) => {
    const newParams = new URLSearchParams(searchParams)
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value)
      } else {
        newParams.delete(key)
      }
    })
    setSearchParams(newParams)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    if (isCustomerView && onPageChange) {
      onPageChange(page)
    } else if (isSearching) {
      handleSearch(page)
    } else {
      fetchAllBookings(page)
    }

    // 更新 URL 參數
    const newParams = new URLSearchParams(searchParams)
    newParams.set('page', page)
    setSearchParams(newParams)
  }

  const handleSearch = async (
    newPage = 1,
    isInitial = false,
    searchOverrides = {}
  ) => {
    if (isCustomerView) return

    if (!isInitial) {
      setSearchLoading(true)
    }
    setCurrentPage(newPage)
    setIsSearching(true)

    const effectiveStylistId =
      searchOverrides.stylistId !== undefined
        ? searchOverrides.stylistId
        : filterStylistId

    try {
      const params = new URLSearchParams({
        page: newPage,
        limit: pageSize
      })

      if (keyword) params.append('keyword', keyword)
      if (selectedStatus) params.append('status', selectedStatus)
      if (selectedDepositStatus) params.append('depositStatus', selectedDepositStatus)
      if (selectedCheckoutStatus) params.append('checkoutStatus', selectedCheckoutStatus)
      if (dateRange) {
        params.append('startDate', dateRange[0].format('YYYY-MM-DD'))
        params.append('endDate', dateRange[1].format('YYYY-MM-DD'))
      }
      if (effectiveStylistId) {
        params.append('stylistId', effectiveStylistId)
      }

      const response = await api.get(
        `/api/business/bookings/search?${params.toString()}`
      )
      setBookings(response.data.bookings)
      setTotalItems(response.data.pagination.totalItems)
    } catch (error) {
      console.error('搜尋預約時出錯:', error)
      message.error('搜尋預約失敗')
    } finally {
      if (!isInitial) {
        setSearchLoading(false)
      }
      setLoading(false) // ← 新增這一行
    }
  }

  const handleReset = () => {
    // 重置 = 店家明確表示「我要看全部」，記下來，下次進站就不再自動篩今日
    writeBookingRangePreset('all')
    setKeyword('')
    setDateRange(null)
    setSelectedStatus(null)
    setSelectedDepositStatus(null)
    setSelectedCheckoutStatus(null)
    setFilterStylistId(null)
    setIsSearching(false)
    setCurrentPage(1)
    setSearchParams({}) // 清所有 URL 參數
    fetchAllBookings(1)
  }

  // 初始加載時根據 URL 參數執行搜索
  useEffect(() => {
    const initializeData = async () => {
      // 這次要套用進站預設區間 → 交給下面監聽 URL 的 effect 去導轉，
      // 這裡先不要打「全部預約」那一槍，否則會多一次請求、還會閃一下全部清單
      if (pendingPresetRef.current) {
        setLoading(true)
        return
      }
      if (!isCustomerView) {
        setLoading(true) // 設置載入狀態
        try {
          if (hasSearchParams) {
            await handleSearch(initialPage, true) // 傳入 true 表示是初始化調用
          } else {
            await fetchAllBookings(initialPage)
          }
        } catch (error) {
          console.error('初始化數據失敗:', error)
          message.error('載入數據失敗')
        } finally {
          setLoading(false) // 確保載入狀態被重置
        }
      } else {
        if (initialBookings) {
          setBookings(initialBookings.bookings || [])
          setTotalItems(initialBookings.pagination?.totalItems || 0)
          setCurrentPage(initialBookings.pagination?.currentPage || 1)
          // 設置顧客頁面的統計數據
          setStats({
            todayBookings: initialBookings.stats?.todayBookings || 0,
            weeklyBookings: initialBookings.stats?.weeklyBookings || 0
          })
        }
      }
    }

    initializeData()
  }, [isCustomerView, initialBookings])

  const [bookings, setBookings] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [modalVisible, setModalVisible] = useState(false)
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [editMode, setEditMode] = useState(false)
  const [editedBooking, setEditedBooking] = useState(null)
  const pageSize = 15
  const [sortField, setSortField] = useState(null)
  const [sortOrder, setSortOrder] = useState('ascend')
  const [loading, setLoading] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [isSearchCollapsed, setIsSearchCollapsed] = useState(true)
  const [stats, setStats] = useState({
    todayBookings: 0,
    weeklyBookings: 0
  })

  /* ─────────── 不指定人員：待指派 ───────────
   *
   * 客人選「不指定」的預約收進來時沒有設計師，店家要主動指派。
   * 這件事沒有人提醒就會被忘記 —— 預約列表裡它長得跟其他單一樣，
   * 只有設計師欄位是空的，很容易滑過去。所以放在頁面最上面一條提示。
   */
  const { can } = usePermission()
  const canAssign = can('assignment:manage')
  const [pendingAssignments, setPendingAssignments] = useState([])
  const [assignTarget, setAssignTarget] = useState(null)


  const loadPendingAssignments = React.useCallback(async () => {
    if (isCustomerView) return
    try {
      const data = await fetchPendingAssignments()
      setPendingAssignments(data.bookings || [])
    } catch (e) {
      // 提示區塊load不到不該影響主要的預約列表，靜默即可
      console.error('載入待指派預約失敗:', e)
    }
  }, [isCustomerView])

  useEffect(() => {
    loadPendingAssignments()
  }, [loadPendingAssignments])

  // 指派完成：待指派清單與列表上那筆的人員欄位都要跟著更新
  const handleAssigned = async () => {
    await loadPendingAssignments()
    if (isSearching) {
      handleSearchSubmit()
    } else {
      fetchAllBookings(currentPage)
    }
  }

  const fetchAllBookings = async (page = 1) => {
    setLoading(true)
    try {
      const response = await api.get('/api/business/bookings', {
        params: {
          page,
          limit: pageSize
        }
      })
      setBookings(response.data.bookings || [])
      setTotalItems(response.data.totalItems || 0)
      setStats({
        todayBookings: response.data.stats.todayBookings || 0,
        weeklyBookings: response.data.stats.weeklyBookings || 0
      })
    } catch (error) {
      console.error('獲取預約列表失敗:', error)
      message.error('獲取預約列表失敗')
      setBookings([])
      setTotalItems(0)
      setStats({
        todayBookings: 0,
        weeklyBookings: 0
      })
    } finally {
      setLoading(false)
    }
  }

  // 預約列表：載入設計師選項（與新增預約表單的 stylists 分開狀態，避免互相覆寫）
  useEffect(() => {
    if (isCustomerView || !businessId) return
    let cancelled = false
    ;(async () => {
      try {
        const { data } = await api.get(`/api/employees/${businessId}`)
        if (!cancelled) {
          setStylistFilterOptions(Array.isArray(data) ? data : [])
        }
      } catch (e) {
        console.error('載入設計師列表失敗:', e)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [businessId, isCustomerView])

  const showModal = (booking) => {
    // 導航到獨立的預約詳情頁面，並傳遞預約數據
    navigate(`/bookings/${booking._id}`, {
      state: { booking }
    })
  }

  // 排序輔助：時間字串 "HH:mm" → 分鐘數（無效值排最後）
  const timeToMinutes = (t) => {
    const [h, m] = String(t || '').split(':')
    const hh = parseInt(h, 10)
    const mm = parseInt(m, 10)
    if (Number.isNaN(hh)) return Number.MAX_SAFE_INTEGER
    return hh * 60 + (Number.isNaN(mm) ? 0 : mm)
  }
  // 日期＋時間 → timestamp（用 dayjs 解析，避免 Safari 對 "YYYY-MM-DD HH:mm" 解析失敗）
  const dateToValue = (b) => {
    const d = dayjs(b.date)
    if (!d.isValid()) return Number.MAX_SAFE_INTEGER
    return d.startOf('day').valueOf() + timeToMinutes(b.time) * 60000
  }

  const sortedBookings = [...bookings].sort((a, b) => {
    if (!sortField) return 0

    let aValue, bValue

    if (sortField === 'date') {
      aValue = dateToValue(a)
      bValue = dateToValue(b)
    } else if (sortField === 'time') {
      aValue = timeToMinutes(a.time)
      bValue = timeToMinutes(b.time)
    } else {
      aValue = a[sortField]
      bValue = b[sortField]
    }

    if (aValue === bValue) return 0
    if (sortOrder === 'ascend') {
      return aValue > bValue ? 1 : -1
    } else {
      return aValue < bValue ? 1 : -1
    }
  })

  // 排序控制：日期排序與時間排序互斥（同一時間僅一種排序生效）
  const handleDateSortChange = (order) => {
    if (order) {
      setSortField('date')
      setSortOrder(order)
    } else if (sortField === 'date') {
      setSortField(null)
    }
  }
  const handleTimeSortChange = (order) => {
    if (order) {
      setSortField('time')
      setSortOrder(order)
    } else if (sortField === 'time') {
      setSortField(null)
    }
  }

  const [timeRange, setTimeRange] = useState(null)

  const handleFilter = () => {
    let filtered = [...bookings]

    if (dateRange) {
      filtered = filtered.filter((booking) => {
        const bookingDate = moment(booking.date)
        return bookingDate.isBetween(dateRange[0], dateRange[1], 'day', '[]')
      })
    }

    if (timeRange) {
      filtered = filtered.filter((booking) => {
        const bookingTime = moment(booking.time, 'HH:mm')
        const startTime = moment(timeRange[0]).format('HH:mm')
        const endTime = moment(timeRange[1]).format('HH:mm')
        return bookingTime.isBetween(
          moment(startTime, 'HH:mm'),
          moment(endTime, 'HH:mm'),
          'minute',
          '[]'
        )
      })
    }

    return filtered
  }

  const handleDelete = async (bookingId) => {
    try {
      Modal.confirm({
        title: '確認刪除',
        content: '確定要刪除這個預約嗎？此操作無法復原。',
        okText: '確認',
        cancelText: '取消',
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await api.delete(`/api/business/bookings/${bookingId}`, {
              data: {
                reason: 'business'
              }
            })
            message.success('預約已刪除')
            // 重新加載預約列表
            if (isSearching) {
              handleSearch(currentPage)
            } else {
              fetchAllBookings(currentPage)
            }
          } catch (error) {
            // 處理特定錯誤
            if (error.response?.data?.error === '只能刪除由店家建立的預約') {
              message.error('只能刪除由店家建立的預約')
            } else {
              message.error('刪除預約失敗')
            }
            console.error('刪除預約失敗:', error)
          }
        }
      })
    } catch (error) {
      console.error('刪除預約失敗:', error)
      message.error('刪除預約失敗')
    }
  }

  // 取消訂單交易
  const handleCancelOrder = async (orderId, bookingId) => {
    try {
      await api.post(
        `/api/businesses/${businessId}/checkout/orders/${orderId}/void`,
        {
          reason: '重新結帳',
          bookingId: bookingId
        }
      )
      message.success('訂單已取消')
      // 重新加載預約列表以更新狀態
      if (isSearching) {
        handleSearch(currentPage)
      } else {
        fetchAllBookings(currentPage)
      }
    } catch (error) {
      console.error('取消訂單失敗:', error)
      throw error
    }
  }

  // 處理改日期
  const handleUpdateDate = (booking) => {
    if (!booking?.orderId) {
      message.warning('此預約沒有訂單，無法修改日期')
      return
    }

    // 設置表單初始值為當前日期和時間
    const currentDate = dayjs(booking.date)
    const currentTime = booking.time || '00:00'

    updateDateForm.setFieldsValue({
      date: currentDate,
      startTime: currentTime,
      endTime: null // 預約可能沒有結束時間
    })

    setSelectedBookingForDateUpdate(booking)
    setIsUpdateDateModalVisible(true)
  }

  // 提交改日期
  const handleUpdateDateSubmit = async () => {
    try {
      const values = await updateDateForm.validateFields()
      const booking = selectedBookingForDateUpdate
      if (!booking?.orderId) return

      const formattedDate = values.date.format('YYYY-MM-DD')
      const startTime = values.startTime || values.date.format('HH:mm')
      const endTime = values.endTime

      const payload = {
        date: formattedDate,
        startTime: startTime
      }

      if (endTime) {
        payload.endTime = endTime
      }

      await api.patch(
        `/api/businesses/${businessId}/checkout/orders/${booking.orderId}/date`,
        payload
      )

      message.success('訂單日期更新成功')
      setIsUpdateDateModalVisible(false)
      updateDateForm.resetFields()
      setSelectedBookingForDateUpdate(null)

      // 重新加載預約列表以更新狀態
      if (isSearching) {
        handleSearch(currentPage)
      } else {
        fetchAllBookings(currentPage)
      }
    } catch (error) {
      console.error('更新訂單日期失敗:', error)
      message.error(error.response?.data?.message || '更新訂單日期失敗')
    }
  }

  const renderTableContent = () => {
    if (!Array.isArray(bookings)) {
      return (
        <div className='empty-container'>
          <Empty description='數據格式錯誤' />
        </div>
      )
    }

    if (bookings.length === 0) {
      return (
        <div className='empty-container'>
          <Empty description='沒有找到相關預約' />
        </div>
      )
    }

    return (
      <>
        <ResponsiveTable>
          {loading ? (
            <div className='empty-container'>
              <Spin size='large'>
                <div style={{ padding: '50px', textAlign: 'center' }}>
                  載入中...
                </div>
              </Spin>
            </div>
          ) : sortedBookings.length === 0 ? (
            <div className='empty-container'>
              <Empty description='暫無預約記錄' />
            </div>
          ) : (
            <>
              <div className='cards-container'>
                {sortedBookings.map((booking) => (
                  <BookingCardComponent
                    key={booking._id}
                    booking={booking}
                    onDelete={handleDelete}
                    onShowModal={showModal}
                    onCancelOrder={handleCancelOrder}
                    onRecheckout={handleCancelOrder}
                    onUpdateDate={handleUpdateDate}
                    customerDisplayNameOverride={
                      isCustomerView && customerInfo
                        ? (() => {
                            const n = getCustomerDisplayData(customerInfo).name
                            return n && n !== '未知' ? n : undefined
                          })()
                        : undefined
                    }
                  />
                ))}
              </div>
              <TableStyles>
                <div className='total-count'>共 {totalItems} 筆預約</div>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={totalItems}
                  onChange={handlePageChange}
                  showTotal={() => null}
                />
              </TableStyles>
            </>
          )}
        </ResponsiveTable>
      </>
    )
  }

  // 監聽 URL 變化的 useEffect
  useEffect(() => {
    // 進站預設區間：寫進網址，實際查詢交給下一輪的這個 effect。
    // 走網址而不是直接設 state，是因為這頁的資料流本來就以 URL 為準
    // （重新整理、分享連結、上一頁都靠它），繞過去會兩邊不同步。
    if (pendingPresetRef.current) {
      const preset = pendingPresetRef.current
      pendingPresetRef.current = null
      const range = bookingRangeOfPreset(preset)
      if (range) {
        setDateRange(range)
        setSearchParams(
          {
            startDate: range[0].format('YYYY-MM-DD'),
            endDate: range[1].format('YYYY-MM-DD'),
            page: '1'
          },
          { replace: true } // 不要在歷史紀錄留下一個「沒有參數」的狀態
        )
        return
      }
    }

    const page = parseInt(searchParams.get('page')) || 1
    const keyword = searchParams.get('keyword') || ''
    const status = searchParams.get('status') || null
    const stylistIdFromUrl = searchParams.get('stylistId') || null
    const startDate = searchParams.get('startDate')
    const endDate = searchParams.get('endDate')
    const urlDateRange =
      startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null
    const urlHasCriteria =
      keyword ||
      status ||
      urlDateRange ||
      stylistIdFromUrl ||
      searchParams.get('depositStatus') ||
      searchParams.get('checkoutStatus')

    // 更新本地狀態
    setCurrentPage(page)
    setKeyword(keyword)
    setSelectedStatus(status)
    setFilterStylistId(stylistIdFromUrl)
    setIsSearching(Boolean(urlHasCriteria))
    if (urlDateRange) {
      setDateRange(urlDateRange)
    }

    // 根據 URL 參數決定執行哪種請求
    if (!isCustomerView) {
      setLoading(true)
      if (urlHasCriteria) {
        handleSearch(page, true, { stylistId: stylistIdFromUrl })
      } else {
        fetchAllBookings(page)
      }
    }
  }, [location.search]) // 當 URL 查詢參數改變時觸發

  // 搜索表單的提交處理
  const handleSearchSubmit = () => {
    const newParams = new URLSearchParams()
    if (keyword) newParams.set('keyword', keyword)
    if (selectedStatus) newParams.set('status', selectedStatus)
    if (selectedDepositStatus) newParams.set('depositStatus', selectedDepositStatus)
    if (selectedCheckoutStatus) newParams.set('checkoutStatus', selectedCheckoutStatus)
    if (filterStylistId) newParams.set('stylistId', filterStylistId)
    if (dateRange) {
      newParams.set('startDate', dateRange[0].format('YYYY-MM-DD'))
      newParams.set('endDate', dateRange[1].format('YYYY-MM-DD'))
    }
    newParams.set('page', '1')
    setSearchParams(newParams)
    // 實際請求由監聽 location.search 的 useEffect 觸發
  }

  // 快速篩選：今天 / 本週 預約
  const handleQuickDateRange = (type) => {
    // 店家時區的今天。用 dayjs() 的話店家人在 UTC+7 的深夜按「今日」，
    // 會抓到台灣的昨天。
    const today = tzNow()
    let start
    let end

    if (type === 'today') {
      start = today.startOf('day')
      end = today.endOf('day')
    } else if (type === 'week') {
      // 週一～週日。dayjs 原生的 week 是週日起算，跟後端的「本週預約」
      // 統計（isoWeek）對不起來，所以這裡也一律走 isoWeek。
      start = today.startOf('isoWeek')
      end = today.endOf('isoWeek')
    } else {
      return
    }

    setDateRange([start, end])
    writeBookingRangePreset(type) // 記住這次的選擇，下次進站直接套用

    const newParams = new URLSearchParams()
    if (keyword) newParams.set('keyword', keyword)
    if (selectedStatus) newParams.set('status', selectedStatus)
    if (selectedDepositStatus) newParams.set('depositStatus', selectedDepositStatus)
    if (selectedCheckoutStatus) newParams.set('checkoutStatus', selectedCheckoutStatus)
    if (filterStylistId) newParams.set('stylistId', filterStylistId)
    newParams.set('startDate', start.format('YYYY-MM-DD'))
    newParams.set('endDate', end.format('YYYY-MM-DD'))
    newParams.set('page', '1')
    setSearchParams(newParams)
  }

  /**
   * 目前套用中的快捷區間。
   * 從 dateRange 反推而不是另外存一份 state —— 重新整理、上一頁、
   * 從別的頁面帶日期進來時都會經過 dateRange，反推才不會跟畫面說謊。
   */
  const activeQuickRange = useMemo(
    () => presetOfBookingRange(dateRange),
    [dateRange]
  )

  // 列表篩選：設計師（與新增預約表單的 selectedStylistId 無關）
  const handleFilterStylistChange = (value) => {
    const id = value || null
    setFilterStylistId(id)
    const newParams = new URLSearchParams(searchParams)
    if (id) newParams.set('stylistId', id)
    else newParams.delete('stylistId')
    newParams.set('page', '1')
    setSearchParams(newParams)
    // 實際請求由監聽 location.search 的 useEffect 觸發
  }

  const [form] = Form.useForm()
  /** 顧客視圖「帶入上一筆」須在 Drawer+Form 掛載後再寫入，否則 initialValues 會蓋掉未掛載時的 setFieldsValue */
  const pendingApplyLastBookingRef = useRef(null)
  const [isAddDrawerVisible, setIsAddDrawerVisible] = useState(false)
  const [customers, setCustomers] = useState([])
  const [showCustomerList, setShowCustomerList] = useState(false)
  const [isComposing, setIsComposing] = useState(false) // 中文輸入法組合狀態
  const [customerNameValue, setCustomerNameValue] = useState('') // 客戶姓名輸入值
  const [products, setProducts] = useState([])
  const [stylists, setStylists] = useState([])
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [selectedServices, setSelectedServices] = useState([]) // 改為多選服務
  const [datePickerVisible, setDatePickerVisible] = useState(false)
  // 服務價格編輯狀態（serviceId -> { basePrice, actualPrice }）
  const [servicePrices, setServicePrices] = useState({})
  // 服務數量狀態（serviceId -> quantity）
  const [serviceQuantities, setServiceQuantities] = useState({})
  // 預約人數
  const [participantCount, setParticipantCount] = useState(1)

  // 新增：服務相關狀態
  // const [additionalServices, setAdditionalServices] = useState([]) // 不再需要
  // const [selectedAdditionalServices, setSelectedAdditionalServices] = useState([]) // 不再需要
  const [showBatchSelector, setShowBatchSelector] = useState(false)
  const [priceCalculation, setPriceCalculation] = useState(null)
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false)
  const [showPricePreview, setShowPricePreview] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 820)
  const [advancedSearchVisible, setAdvancedSearchVisible] = useState(false)

  // 新增：休假相關狀態
  const [businessDaysOff, setBusinessDaysOff] = useState([]) // 店家休假
  const [employeeDaysOff, setEmployeeDaysOff] = useState([]) // 員工休假
  const [selectedStylistId, setSelectedStylistId] = useState(null) // 選中的員工ID
  const [unavailableTimeSlots, setUnavailableTimeSlots] = useState([]) // 不可用的時間段
  const [isUpdateDateModalVisible, setIsUpdateDateModalVisible] =
    useState(false) // 改日期 Modal 顯示狀態
  const [selectedBookingForDateUpdate, setSelectedBookingForDateUpdate] =
    useState(null) // 選中用於改日期的預約
  const [updateDateForm] = Form.useForm() // 改日期表單
  const [updateDatePickerVisible, setUpdateDatePickerVisible] = useState(false) // 改日期日期選擇器顯示狀態

  // 獲取店家休假
  const fetchBusinessDaysOff = async () => {
    try {
      const response = await api.get(`/api/business_B/${businessId}/dayoff`)
      // 響應格式：response.data.data
      setBusinessDaysOff(response.data.data || [])
    } catch (error) {
      console.error('獲取店家休假失敗:', error)
      // 如果API不存在，設置為空數組
      setBusinessDaysOff([])
    }
  }

  // 獲取員工休假
  const fetchEmployeeDaysOff = async (employeeId) => {
    if (!employeeId) {
      setEmployeeDaysOff([])
      return
    }
    try {
      const response = await api.get(
        `/api/employees/${businessId}/${employeeId}/dayoff`
      )
      // 響應格式：response.data.data
      setEmployeeDaysOff(response.data.data || [])
    } catch (error) {
      console.error('獲取員工休假失敗:', error)
      // 如果API不存在，設置為空數組
      setEmployeeDaysOff([])
    }
  }

  // 計算不可用的時間段（結合店家休假和員工休假）
  useEffect(() => {
    if (!selectedStylistId) {
      setUnavailableTimeSlots([])
      return
    }

    const unavailable = []

    // 合併店家休假和員工休假
    const allDaysOff = [...businessDaysOff, ...employeeDaysOff]

    allDaysOff.forEach((dayOff) => {
      const start = dayjs(dayOff.start).tz('Asia/Taipei')
      const end = dayjs(dayOff.end).tz('Asia/Taipei')

      // 生成不可用的時間段
      let current = start.clone()
      while (current.isBefore(end) || current.isSame(end, 'minute')) {
        unavailable.push(current.format('YYYY-MM-DD HH:mm'))
        current = current.add(15, 'minute') // 15分鐘間隔
      }
    })

    setUnavailableTimeSlots(unavailable)
  }, [selectedStylistId, businessDaysOff, employeeDaysOff])

  // 當選擇員工時，獲取該員工的休假
  const handleStylistChange = (stylistId) => {
    setSelectedStylistId(stylistId)
    form.setFieldsValue({ stylistId })
    fetchEmployeeDaysOff(stylistId)
  }

  // 禁用不可用的日期時間
  const disabledDateTime = (current) => {
    if (!current) return {}

    const currentStr = current.format('YYYY-MM-DD HH:mm')

    // 禁用休假時間（店家或員工）
    const isUnavailable = unavailableTimeSlots.some(
      (slot) => slot === currentStr
    )

    if (isUnavailable) {
      return {
        disabledHours: () => [current.hour()],
        disabledMinutes: () => [current.minute()]
      }
    }

    return {}
  }

  const renderDateTimePicker = () => {
    const currentValue = form.getFieldValue('date')

    return (
      <Form.Item
        name='date'
        label='預約日期和時間'
        rules={[{ required: true, message: '請選擇預約日期和時間' }]}
        validateTrigger={['onChange', 'onBlur']} // 添加验证触发器
      >
        <div className='date-picker-wrapper'>
          <DesktopPicker>
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format='YYYY-MM-DD HH:mm'
              style={{ width: '100%' }}
              onChange={(date) => {
                // 确保日期值被正确设置到表单中
                form.setFieldsValue({ date })
                // 立即触发验证
                form.validateFields(['date'])
              }}
            />
          </DesktopPicker>

          <MobilePicker>
            <MobileDatePicker
              visible={datePickerVisible}
              onClose={() => setDatePickerVisible(false)}
              precision='minute'
              filter={{ minute: (val) => val % 5 === 0 }}
              value={currentValue ? currentValue.toDate() : tzNow().toDate()}
              onConfirm={(val) => {
                const newDate = dayjs(val)
                form.setFieldsValue({ date: newDate })
                setDatePickerVisible(false)
              }}
              popupStyle={{
                // 添加彈出層樣式
                zIndex: 1500 // 確保在最上層
              }}
              getContainer={null} // 讓彈出層掛載到 body 上
            />
            <div
              onClick={() => setDatePickerVisible(true)}
              style={{
                cursor: 'pointer',
                padding: '4px 11px',
                border: '1px solid #d9d9d9',
                borderRadius: '2px',
                backgroundColor: '#fff'
              }}
            >
              {currentValue
                ? dayjs(currentValue).format('YYYY-MM-DD HH:mm')
                : '選擇日期和時間'}
            </div>
          </MobilePicker>
        </div>
      </Form.Item>
    )
  }

  // 獲取產品和造型師數據，回傳 products 陣列供呼叫端同步使用（setState 非同步）
  const fetchProductsAndStylists = async () => {
    setIsLoadingData(true)
    try {
      const [productsResponse, stylistsResponse] = await Promise.all([
        api.get(`/api/business_B/${businessId}/products`),
        api.get(`/api/employees/${businessId}`)
      ])

      const raw = productsResponse.data
      const productList = Array.isArray(raw)
        ? raw
        : raw?.products || raw?.data || []
      const list = Array.isArray(productList) ? productList : []
      setProducts(list)
      setStylists(stylistsResponse.data)
      return list
    } catch (error) {
      console.error('獲取數據失敗:', error)
      message.error('獲取數據失敗，請重試')
      return []
    } finally {
      setIsLoadingData(false)
    }
  }

  // 依日期時間排序取最新一筆
  const pickLastFromList = (list) => {
    if (!list?.length) return null
    const sorted = [...list].sort((a, b) => {
      const aVal = new Date(`${a.date} ${a.time || '00:00'}`).getTime()
      const bVal = new Date(`${b.date} ${b.time || '00:00'}`).getTime()
      return bVal - aVal
    })
    return sorted[0]
  }

  // 取得客戶上一筆預約（依日期時間排序，取最新一筆）
  // 同時從 state 與 prop 取列表，相容 API 回傳 { bookings } 或 { data: { bookings } } 及延遲載入
  const getLastBooking = () => {
    if (!isCustomerView) return null
    const listFromProp = Array.isArray(initialBookings)
      ? initialBookings
      : initialBookings?.bookings || initialBookings?.data?.bookings || []
    const list = (bookings?.length ? bookings : listFromProp) || []
    return pickLastFromList(list)
  }

  // 依 customerId 請求該客戶的預約列表並回傳上一筆（共用：客戶視圖開 Drawer 前 / 表單選擇客戶後）
  const fetchLastBookingByCustomerId = async (customerId) => {
    if (!customerId) return null
    try {
      const res = await api.get(`/api/business/bookings/customer/${customerId}`, {
        params: { page: 1, limit: 50 }
      })
      const raw = res?.data
      const list =
        Array.isArray(raw?.bookings)
          ? raw.bookings
          : Array.isArray(raw?.data?.bookings)
            ? raw.data.bookings
            : Array.isArray(raw)
              ? raw
              : []
      return pickLastFromList(list)
    } catch (e) {
      console.warn('取得客戶上一筆預約失敗', e)
      return null
    }
  }

  // 客戶視圖下主動請求該客戶的預約列表並回傳上一筆（不依賴 Drawer 列表是否已載入）
  const fetchLastBookingForCustomer = async () => {
    const customerId = customerInfo?._id
    if (!isCustomerView || !customerId) return null
    return fetchLastBookingByCustomerId(customerId)
  }

  // 帶入上一筆預約至表單（僅設定欄位，不開 Drawer）。productsToUse 為可選，避免依賴尚未更新的 state
  const applyLastBooking = (lastBooking, productsToUse) => {
    if (!lastBooking) return
    const list =
      productsToUse && productsToUse.length > 0 ? productsToUse : products
    try {
      // 自動帶入不覆寫日期／時間，保留使用者已選好的值
      form.setFieldsValue({
        stylistId: lastBooking.stylistId || undefined,
        note: lastBooking.note || undefined
      })
      setSelectedStylistId(lastBooking.stylistId || null)

      if (list.length > 0) {
        const normName = (s) => String(s || '').replace(/\s+/g, '').trim()
        const findProduct = (rawId, serviceName) => {
          if (rawId != null && String(rawId).trim() !== '') {
            const byId = list.find((p) => String(p._id) === String(rawId))
            if (byId) return byId
          }
          const n = String(serviceName || '').trim()
          if (n) {
            const byExact = list.find((p) => (p.name || '').trim() === n)
            if (byExact) return byExact
            const nn = normName(n)
            if (nn) {
              const byNorm = list.find((p) => normName(p.name) === nn)
              if (byNorm) return byNorm
            }
          }
          return null
        }

        const resolveProductIds = () => {
          const ids = []
          if (lastBooking.services?.length) {
            for (const s of lastBooking.services) {
              const raw = s.serviceId ?? s.productId
              const p = findProduct(raw, s.serviceName)
              if (p) ids.push(p._id)
            }
          }
          if (ids.length === 0 && lastBooking.productId) {
            const p = findProduct(
              lastBooking.productId,
              lastBooking.productName
            )
            if (p) ids.push(p._id)
          }
          return ids
        }

        const productIds = resolveProductIds()
        if (productIds.length > 0) {
          handleServicesSelect(productIds)
          const prices = {}
          const qty = {}
          if (lastBooking.services?.length) {
            lastBooking.services.forEach((s) => {
              const raw = s.serviceId ?? s.productId
              const p = findProduct(raw, s.serviceName)
              if (!p) return
              const sid = p._id
              if (s.actualPrice != null) {
                const base = p.price ?? 0
                prices[sid] = {
                  basePrice: base,
                  actualPrice: s.actualPrice
                }
              }
              if (s.quantity != null) qty[sid] = s.quantity
            })
          } else if (lastBooking.productId) {
            const p = findProduct(
              lastBooking.productId,
              lastBooking.productName
            )
            if (p) {
              const sid = p._id
              const base = p.price ?? 0
              const totalDiscount =
                lastBooking.discountedPrice ?? lastBooking.originalPrice
              if (totalDiscount != null) {
                prices[sid] = {
                  basePrice: base,
                  actualPrice: totalDiscount
                }
              }
              qty[sid] = 1
            }
          }
          if (Object.keys(prices).length > 0) {
            setServicePrices((prev) => ({ ...prev, ...prices }))
          }
          if (Object.keys(qty).length > 0) {
            setServiceQuantities((prev) => ({ ...prev, ...qty }))
          }
          const totalDiscount =
            lastBooking.discountedPrice ?? lastBooking.originalPrice
          if (totalDiscount != null) {
            form.setFieldsValue({ discountedPrice: totalDiscount })
          }
        }
      }
    } catch (e) {
      console.warn('帶入上一筆預約失敗', e)
    }
  }

  // 顯示新增預約 Drawer
  const showAddDrawer = async () => {
    const fetchedProducts = await fetchProductsAndStylists()
    await fetchBusinessDaysOff() // 獲取店家休假
    setSelectedStylistId(null)
    setUnavailableTimeSlots([])
    if (isCustomerView && customerInfo) {
      const d = getCustomerDisplayData(customerInfo)
      if (d.name && d.name !== '未知') {
        setCustomerNameValue(d.name)
      }
    }

    // 客戶視圖：主動請求該客戶的預約列表取上一筆，避免 Drawer 列表尚未載入而取不到
    const lastBooking = isCustomerView
      ? await fetchLastBookingForCustomer()
      : getLastBooking()
    if (lastBooking) {
      Modal.confirm({
        title: '是否帶入上一筆預約？',
        content:
          '可將上一筆預約的設計師、服務與金額帶入表單（不含日期時間），方便快速建立類似預約。',
        okText: '帶入',
        cancelText: '不帶入',
        getContainer: () => document.body,
        onOk: () => {
          pendingApplyLastBookingRef.current = {
            lastBooking,
            products: fetchedProducts
          }
          setIsAddDrawerVisible(true)
        },
        onCancel: () => {
          setIsAddDrawerVisible(true)
        }
      })
    } else {
      setIsAddDrawerVisible(true)
    }
  }

  // 上一筆預約：待 Drawer 開啟且表單掛載後再帶入（見 pendingApplyLastBookingRef）
  useEffect(() => {
    if (!isAddDrawerVisible) return
    const pending = pendingApplyLastBookingRef.current
    if (!pending) return
    if (isLoadingData) return
    const { lastBooking, products: pl } = pending
    const list = pl && pl.length > 0 ? pl : products
    if (!list.length) {
      if (!isLoadingData) pendingApplyLastBookingRef.current = null
      return
    }
    pendingApplyLastBookingRef.current = null
    applyLastBooking(lastBooking, list)
  }, [isAddDrawerVisible, isLoadingData, products])

  // 關閉 Drawer
  const closeAddDrawer = () => {
    pendingApplyLastBookingRef.current = null
    setIsAddDrawerVisible(false)
    form.resetFields()
    setCustomerNameValue('')
    setProducts([])
    setStylists([])
    setSelectedServices([])
    setServicePrices({})
    setServiceQuantities({})
    setParticipantCount(1)
    setSelectedServices([])
    setServicePrices({}) // 重置服務價格狀態
    setPriceCalculation(null)
    setCustomers([])
    setShowCustomerList(false)
    setSelectedStylistId(null)
    setUnavailableTimeSlots([])
    setBusinessDaysOff([])
    setEmployeeDaysOff([])
  }

  // 搜索客戶
  const searchCustomers = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setCustomers([])
      setShowCustomerList(false)
      return
    }

    const setResult = (data) => {
      const list = Array.isArray(data) ? data : []
      setCustomers(list)
      setShowCustomerList(list.length > 0)
    }

    try {
      let response = await api.get(
        `/api/business_B/${businessId}/customers/search`,
        { params: { searchTerm } }
      ).catch((err) => {
        if (err?.response?.status === 404) return null
        throw err
      })
      if (response?.data?.success) {
        setResult(response.data.data)
        return
      }
      if (response) {
        setResult(response.data?.data || [])
        return
      }
      // 404：嘗試用列表 API 的查詢參數搜尋（部分後端以 searchTerm 掛在 GET /customers 上）
      response = await api.get(
        `/api/business_B/${businessId}/customers`,
        { params: { searchTerm, page: 1, limit: 50 } }
      )
      const raw = response?.data
      const list = raw?.data ?? raw?.customers ?? (Array.isArray(raw) ? raw : [])
      setResult(list)
    } catch (error) {
      console.error('搜索客戶失敗:', error)
      setCustomers([])
      setShowCustomerList(false)
    }
  }

  // 選擇客戶
  const selectCustomer = async (customer) => {
    const displayData = getCustomerDisplayData(customer)
    const customerName =
      displayData.name && displayData.name !== '未知' ? displayData.name : ''
    const customerPhone =
      displayData.phone && displayData.phone !== '未提供'
        ? displayData.phone
        : ''
    const customerEmail =
      displayData.email && displayData.email !== '未知'
        ? displayData.email
        : ''
    const customerId = customer._id || customer.customerId || null

    setCustomerNameValue(customerName)
    form.setFieldsValue({
      userName: customerName,
      phone: customerPhone,
      email: customerEmail,
      customerId: customerId,
      customerType: 'existing' // 從顧客名單選取的人自動帶入舊客戶
    })
    setShowCustomerList(false)
    setCustomers([])

    // 非客戶視圖：在表單中選擇客戶後，詢問是否帶入該客戶的上一筆預約
    if (!isCustomerView && customerId) {
      const lastBooking = await fetchLastBookingByCustomerId(customerId)
      if (lastBooking) {
        Modal.confirm({
          title: '是否帶入上一筆預約？',
          content:
            '可將上一筆預約的設計師、服務與金額帶入表單（不含日期時間），方便快速建立類似預約。',
          okText: '帶入',
          cancelText: '不帶入',
          getContainer: () => document.body,
          onOk: () => {
            applyLastBooking(lastBooking, products?.length ? products : undefined)
          }
        })
      }
    }
  }

  // 處理輸入變更
  const handleInputChange = (field, value) => {
    // 更新表單值
    form.setFieldsValue({ [field]: value })

    // 只有客戶姓名欄位才觸發搜尋
    if (field === 'userName') {
      // 只有在輸入法組合結束後才觸發搜尋
      if (!isComposing) {
        searchCustomers(value)
      }
    }
  }

  // 點擊外部關閉搜索結果
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showCustomerList &&
        !event.target.closest('.customer-search-container')
      ) {
        setShowCustomerList(false)
      }
    }

    if (showCustomerList) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showCustomerList])

  // 新增：價格計算功能
  const calculatePrice = async (selectedServices, additionalServicesList) => {
    try {
      setIsCalculatingPrice(true)
      const response = await api.post('/api/business/calculate-price', {
        selectedServices: selectedServices || [],
        additionalServices: additionalServicesList || []
      })

      if (response.data.success) {
        return response.data.priceCalculation
      } else {
        throw new Error(response.data.message || '價格計算失敗')
      }
    } catch (error) {
      console.error('價格計算失敗:', error)
      // 如果API失敗，使用本地計算
      return calculateLocalPrice(selectedServices, additionalServicesList)
    } finally {
      setIsCalculatingPrice(false)
    }
  }

  // 本地價格計算
  const calculateLocalPrice = (selectedServices, additionalServicesList) => {
    const basePrice = selectedServices?.price || 0
    const additionalPrice = additionalServicesList.reduce(
      (sum, service) => sum + service.price,
      0
    )
    const totalDuration =
      (selectedServices?.duration || 0) +
      additionalServicesList.reduce((sum, service) => sum + service.duration, 0)

    return {
      originalTotal: basePrice + additionalPrice,
      finalPrice: basePrice + additionalPrice,
      totalDuration,
      serviceDetails: selectedServices
        ? [
            {
              serviceName: selectedServices.name,
              price: selectedServices.price,
              duration: selectedServices.duration
            }
          ]
        : [],
      additionalServicesDetails: additionalServicesList.map((service) => ({
        serviceName: service.name,
        price: service.price,
        duration: service.duration
      }))
    }
  }

  // 處理服務選擇
  const handleServicesSelect = (selectedIds) => {
    // 找出所有選中的服務對象
    const selected = selectedIds
      .map((id) => products.find((p) => String(p._id) === String(id)))
      .filter(Boolean)

    setSelectedServices(selected)

    // 初始化服務價格狀態
    const initialServicePrices = {}
    const initialServiceQuantities = {}
    selected.forEach((service) => {
      const serviceId = service._id
      const basePrice = service.price || 0
      initialServicePrices[serviceId] = {
        basePrice,
        actualPrice: basePrice // 默認實際價格等於原價
      }
      // 初始化服務數量為1
      initialServiceQuantities[serviceId] = serviceQuantities[serviceId] || 1
    })
    setServicePrices(initialServicePrices)
    setServiceQuantities((prev) => ({ ...prev, ...initialServiceQuantities }))

    // 計算原價（basePrice 總和）和優惠價（actualPrice 總和）
    const totalBasePrice = selected.reduce((sum, s) => sum + (s.price || 0), 0)
    const totalActualPrice = selected.reduce(
      (sum, s) => sum + (s.price || 0),
      0
    )

    // 更新表單值
    form.setFieldsValue({
      productIds: selectedIds,
      originalPrice: totalBasePrice, // 原價 = basePrice 總和
      discountedPrice: totalActualPrice // 優惠價 = actualPrice 總和
    })
  }

  // 處理新增預約
  const handleAddBooking = async (values) => {
    try {
      const dateObj = values.date.toDate()
      const formattedDate = dayjs(dateObj).format('YYYY-MM-DD')
      const formattedTime = dayjs(dateObj).format('HH:mm')

      const selectedStylist = stylists.find(
        (stylist) => stylist._id === values.stylistId
      )

      // 構建 selectedServices 數組（包含 basePrice, actualPrice 和 quantity）
      const selectedServicesForSubmit = selectedServices.map((service) => {
        const serviceId = service._id
        const basePrice = service.price || 0
        const priceInfo = servicePrices[serviceId]
        const actualPrice = priceInfo?.actualPrice ?? basePrice
        const quantity = serviceQuantities[serviceId] || 1

        return {
          serviceId,
          basePrice,
          actualPrice,
          quantity
        }
      })

      // 構建服務結構（用於向後兼容）
      const servicesArray = selectedServices.map((service) => {
        const serviceId = service._id
        const basePrice = service.price || 0
        const priceInfo = servicePrices[serviceId]
        const actualPrice = priceInfo?.actualPrice ?? basePrice
        const quantity = serviceQuantities[serviceId] || 1

        return {
          serviceId,
          serviceName: service.name,
          basePrice,
          actualPrice,
          duration: service.duration,
          category: 'main',
          quantity
        }
      })

      // 計算總價格和總時長（使用 actualPrice * quantity）
      const totalPrice = selectedServicesForSubmit.reduce(
        (sum, service) =>
          sum + (service.actualPrice || 0) * (service.quantity || 1),
        0
      )
      const totalDuration = selectedServices.reduce(
        (sum, service) =>
          sum + (service.duration || 0) * (serviceQuantities[service._id] || 1),
        0
      )

      // 基本預約數據
      const bookingData = {
        businessId: businessId,
        status: 'confirmed',
        // 使用第一個服務作為主要顯示，或者保留 productId 欄位但存儲主要服務ID
        productId: servicesArray[0]?.serviceId,
        productName: servicesArray[0]?.serviceName,
        stylistId: values.stylistId,
        stylistName: selectedStylist?.name,
        duration: totalDuration,
        depositStatus: values.depositStatus || 'not_required',
        date: formattedDate,
        time: formattedTime,
        // 新的服務結構
        services: servicesArray,
        // 發送 selectedServices 數組用於價格更新（包含 serviceId, basePrice, actualPrice, quantity）
        selectedServices: selectedServicesForSubmit,
        additionalServices: [], // 在多選模式下，所有服務都視為 selectedServices
        // 價格相關欄位
        originalPrice: totalPrice,
        discountedPrice: values.discountedPrice || totalPrice,
        customerType: values.customerType || 'new',
        customerSource: values.customerSource || 'none',
        // 預約人數和服務數量
        participantCount: values.participantCount || participantCount || 1,
        notifyCustomer: values.notifyCustomer !== false
      }

      // 根據是否為顧客視圖添加不同的用戶資訊
      if (isCustomerView && customerInfo) {
        const d = getCustomerDisplayData(customerInfo)
        const pu = resolvePlatformUser(customerInfo)
        Object.assign(bookingData, {
          userName:
            (d.name && d.name !== '未知' && d.name) || pu?.name || '',
          phone: (d.phone && d.phone !== '未提供' && d.phone) || pu?.phone || '',
          email:
            (d.email && d.email !== '未知' && d.email) || pu?.email || '',
          customerId: customerInfo._id
        })
      } else {
        // 從店家管理頁面添加
        Object.assign(bookingData, {
          userName: values.userName,
          phone: values.phone,
          email: values.email,
          customerId: values.customerId || null // 如果從客戶列表選擇，會有 customerId
        })
      }

      // 添加備註（如果有的話）
      if (values.note) {
        bookingData.note = values.note
      }

      const response = await api.post('/api/business/bookings', bookingData)

      if (response.status === 201) {
        message.success('預約新增成功')
        closeAddDrawer()

        // 根據視圖類型選擇不同的刷新方式
        if (isCustomerView && onPageChange) {
          // 在顧客視圖中，調用父組件傳入的 onPageChange 來刷新顧客的預約列表
          onPageChange(currentPage)
        } else {
          // 在店家管理視圖中，刷新所有預約
          fetchAllBookings(currentPage)
        }
      }
    } catch (error) {
      console.error('新增預約失敗:', error)
      message.error('新增預約失敗，請重試')
    }
  }

  // 修改 handleMobileDateChange 函數
  const handleMobileDateChange = (val) => {
    // MobileDatePicker 返回的是 Date 对象，需要转换为 dayjs 对象
    const selectedDate = dayjs(val)

    // 强制更新表单值
    form.setFieldsValue({
      date: selectedDate
    })

    // 强制触发表单值更新
    form.validateFields(['date'])
  }

  // 監聽表單值變化
  useEffect(() => {
    const subscription =
      form.getFieldInstance('date')?.props?.onChange &&
      form.getFieldInstance('date').props.onChange((date) => {
        // 強制更新組件
        forceUpdate({})
      })

    return () => {
      if (subscription) {
        subscription.unsubscribe()
      }
    }
  }, [form])

  // 用於強制更新組件的 state
  const [, forceUpdate] = useState({})

  // 檢測屏幕尺寸
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 820)
    }

    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)

    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  return (
    <>
      <PageHeader>
        <div className='right-section'>
          <div className='stats-section' style={{ display: 'flex' }}>
            <div className='stats-item'>
              <div className='stats-label'>今日預約</div>
              <div className='stats-value'>{stats.todayBookings}</div>
            </div>
            <div className='stats-item'>
              <div className='stats-label'>本週預約</div>
              <div className='stats-value'>{stats.weeklyBookings}</div>
            </div>
          </div>
        </div>
        <div className='add-button-wrapper'>
          <Button
            type='primary'
            icon={<PlusOutlined />}
            onClick={showAddDrawer}
            className='add-button'
            size='middle'
            style={{ width: '100%' }}
          >
            {isCustomerView
              ? (() => {
                  const n = customerInfo
                    ? getCustomerDisplayData(customerInfo).name
                    : ''
                  return `新增${n && n !== '未知' ? n : '此客戶'}的預約`
                })()
              : '新增預約'}
          </Button>
        </div>
      </PageHeader>

      {!isCustomerView && pendingAssignments.length > 0 && (
        <Hint
          tone='warning'
          title={`有 ${pendingAssignments.length} 筆預約還沒指派人員`}
          style={{ marginBottom: 16 }}
          action={
            <Space wrap size={[8, 8]}>
              {pendingAssignments.slice(0, 3).map((b) => (
                <Button
                  key={b._id}
                  size='small'
                  type={canAssign ? 'primary' : 'default'}
                  disabled={!canAssign}
                  onClick={() => setAssignTarget(b)}
                >
                  {dayjs(b.date).format('M/D')} {b.time}　{b.userName || '客人'}
                </Button>
              ))}
              <Button
                size='small'
                onClick={() => navigate('/bookings/pending-assignment')}
              >
                {pendingAssignments.length > 3
                  ? `查看全部 ${pendingAssignments.length} 筆`
                  : '待指派清單'}
                <RightOutlined style={{ fontSize: 10, marginLeft: 4 }} />
              </Button>
            </Space>
          }
        >
          {canAssign
            ? '這些客人預約時選了「不指定人員」，時段已經幫他們留下來了，但還沒有人負責。點預約可以直接指派，或到待指派清單一次處理。'
            : '這些客人預約時選了「不指定人員」，需要店主或店長指派負責的人。'}
        </Hint>
      )}

      <AssignStylistModal
        booking={assignTarget}
        onClose={() => setAssignTarget(null)}
        onAssigned={handleAssigned}
      />

      {!isCustomerView && (
        <>
          <SearchSection>
            <div className='search-container'>
              <div className='search-input'>
                <Input
                  placeholder='搜索客戶姓名或預約編號'
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  style={{ minWidth: '100px', maxWidth: '500px' }}
                  prefix={<SearchOutlined />}
                  onPressEnter={handleSearchSubmit}
                  allowClear
                />
              </div>

              <div className='search-toolbar-row'>
                <div className='search-quick-row'>
                  <Button
                    type='primary'
                    style={{ width: '100%' }}
                    onClick={handleSearchSubmit}
                    loading={searchLoading}
                    className='compact-btn'
                  >
                    搜索
                  </Button>
                  <Button style={{ width: '100%' }} onClick={handleReset} className='reset-btn'>
                    重置
                  </Button>
                  <Button
                    className='quick-range-btn'
                    type={activeQuickRange === 'today' ? 'primary' : 'default'}
                    onClick={() => handleQuickDateRange('today')}
                  >
                    今日
                  </Button>
                  <Button
                    className='quick-range-btn'
                    type={activeQuickRange === 'week' ? 'primary' : 'default'}
                    onClick={() => handleQuickDateRange('week')}
                  >
                    本週
                  </Button>
                </div>

                <div className='action-buttons'>
                  <Select
                    className='booking-sort-select'
                    placeholder='日期排序'
                    allowClear
                    style={{ minWidth: 130 }}
                    value={sortField === 'date' ? sortOrder : undefined}
                    onChange={handleDateSortChange}
                  >
                    <Option value='ascend'>日期：舊 → 新</Option>
                    <Option value='descend'>日期：新 → 舊</Option>
                  </Select>
                  <Select
                    className='booking-sort-select'
                    placeholder='時間排序'
                    allowClear
                    style={{ minWidth: 130 }}
                    value={sortField === 'time' ? sortOrder : undefined}
                    onChange={handleTimeSortChange}
                  >
                    <Option value='ascend'>時間：早 → 晚</Option>
                    <Option value='descend'>時間：晚 → 早</Option>
                  </Select>
                  <Select
                    className='booking-stylist-select'
                    placeholder='服務人員(可搜尋)'
                    allowClear
                    optionFilterProp='children'
                    style={{ minWidth: 140, maxWidth: 220 }}
                    value={filterStylistId || undefined}
                    onChange={handleFilterStylistChange}
                    filterOption={(input, option) =>
                      (option?.children ?? '')
                        .toString()
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {stylistFilterOptions.map((s) => (
                      <Option key={s._id} value={s._id}>
                        {s.name}
                      </Option>
                    ))}
                  </Select>
                  <Button
                    className='mobile-advanced-btn'
                    onClick={() => setAdvancedSearchVisible(true)}
                  >
                    <SearchOutlined />
                    進階
                  </Button>
                </div>
              </div>
            </div>

            {/* 有了進站預設值之後，店家一定要看得出畫面是被篩過的，
                否則會以為「預約怎麼變少了」。順便給一個回到全部的出口。 */}
            {!isCustomerView && dateRange && (
              <div className='range-hint'>
                <span>
                  目前顯示{' '}
                  <strong>
                    {formatBookingRangeLabel(dateRange, activeQuickRange)}
                  </strong>{' '}
                  的預約，共 {totalItems} 筆
                </span>
                <Button type='link' size='small' onClick={handleReset}>
                  顯示全部
                </Button>
              </div>
            )}
          </SearchSection>

          <AdvancedSearchDrawer
            title='進階搜索'
            placement={window.innerWidth <= 768 ? 'bottom' : 'right'}
            height={window.innerWidth <= 768 ? '80%' : '100%'}
            width={window.innerWidth <= 768 ? undefined : 400}
            open={advancedSearchVisible}
            onClose={() => setAdvancedSearchVisible(false)}
            extra={
              <Button
                type='primary'
                onClick={() => {
                  handleSearchSubmit()
                  setAdvancedSearchVisible(false)
                }}
                loading={searchLoading}
              >
                搜索
              </Button>
            }
          >
            <div className='drawer-content'>
              <div className='filter-section'>
                <div className='section-title'>
                  <SearchOutlined />
                  搜索條件
                </div>
                <div className='filter-grid'>
                  <Input
                    placeholder='搜索客戶姓名或預約編號'
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    prefix={<SearchOutlined />}
                    allowClear
                  />
                </div>
              </div>

              <div className='filter-section'>
                <div className='section-title'>狀態篩選</div>
                <div className='filter-grid'>
                  <Select
                    placeholder='選擇預約狀態'
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                    allowClear
                    style={{ width: '100%' }}
                  >
                    <Option value='pending'>等待確認</Option>
                    <Option value='confirmed'>已確認</Option>
                    <Option value='completed'>已完成</Option>
                    <Option value='canceled'>已取消</Option>
                    <Option value='no_show'>未出席</Option>
                  </Select>
                </div>
              </div>

              <div className='filter-section'>
                <div className='section-title'>定金狀態</div>
                <div className='filter-grid'>
                  <Select
                    placeholder='選擇定金狀態'
                    value={selectedDepositStatus}
                    onChange={setSelectedDepositStatus}
                    allowClear
                    style={{ width: '100%' }}
                  >
                    <Option value='unpaid'>未付定金</Option>
                    <Option value='paid'>已付定金</Option>
                    <Option value='not_required'>不需要定金</Option>
                  </Select>
                </div>
              </div>

              <div className='filter-section'>
                <div className='section-title'>結帳狀態</div>
                <div className='filter-grid'>
                  <Select
                    placeholder='選擇結帳狀態'
                    value={selectedCheckoutStatus}
                    onChange={setSelectedCheckoutStatus}
                    allowClear
                    style={{ width: '100%' }}
                  >
                    <Option value='checked_out'>已結帳</Option>
                    <Option value='not_checked_out'>未結帳</Option>
                  </Select>
                </div>
              </div>

              <div className='filter-section'>
                <div className='section-title'>設計師篩選</div>
                <div className='filter-grid'>
                  <Select
                    placeholder='設計師篩選...(可搜尋)'
                    allowClear
                    showSearch
                    optionFilterProp='children'
                    style={{ width: '100%' }}
                    value={filterStylistId || undefined}
                    onChange={handleFilterStylistChange}
                    filterOption={(input, option) =>
                      (option?.children ?? '')
                        .toString()
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {stylistFilterOptions.map((s) => (
                      <Option key={s._id} value={s._id}>
                        {s.name}
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>

              <div className='filter-section'>
                <div className='section-title'>日期範圍</div>
                <div className='filter-grid' style={{ gap: '12px' }}>
                  {/* 開始日期選擇 */}
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      marginBottom: '12px',
                      alignItems: 'center'
                    }}
                  >
                    <span
                      style={{
                        minWidth: '44px',
                        flexShrink: 0,
                        fontSize: '14px',
                        color: '#666'
                      }}
                    >
                      起始：
                    </span>
                    <Select
                      placeholder='年'
                      value={dateRange?.[0]?.year()}
                      onChange={(year) => {
                        const currentStart = dateRange?.[0] || tzNow()
                        const newStart = currentStart.year(year)
                        if (dateRange?.[1]) {
                          setDateRange([newStart, dateRange[1]])
                        } else {
                          setDateRange([newStart, newStart.add(1, 'month')])
                        }
                      }}
                      style={{ flex: '1.2 1 0', minWidth: 92 }}
                    >
                      {Array.from(
                        { length: 5 },
                        (_, i) => tzNow().year() - 2 + i
                      ).map((year) => (
                        <Option key={year} value={year}>
                          {year}年
                        </Option>
                      ))}
                    </Select>
                    <Select
                      placeholder='月'
                      value={
                        dateRange?.[0] ? dateRange[0].month() + 1 : undefined
                      }
                      onChange={(month) => {
                        const currentStart = dateRange?.[0] || tzNow()
                        const newStart = currentStart.month(month - 1)
                        if (dateRange?.[1]) {
                          setDateRange([newStart, dateRange[1]])
                        } else {
                          setDateRange([newStart, newStart.add(1, 'month')])
                        }
                      }}
                      style={{ flex: '1 1 0', minWidth: 84 }}
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(
                        (month) => (
                          <Option key={month} value={month}>
                            {month}月
                          </Option>
                        )
                      )}
                    </Select>
                    <Select
                      placeholder='日'
                      value={dateRange?.[0]?.date()}
                      onChange={(day) => {
                        const currentStart = dateRange?.[0] || tzNow()
                        const newStart = currentStart.date(day)
                        if (dateRange?.[1]) {
                          setDateRange([newStart, dateRange[1]])
                        } else {
                          setDateRange([newStart, newStart.add(7, 'day')])
                        }
                      }}
                      style={{ flex: '1 1 0', minWidth: 84 }}
                    >
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(
                        (day) => (
                          <Option key={day} value={day}>
                            {day}日
                          </Option>
                        )
                      )}
                    </Select>
                  </div>

                  {/* 結束日期選擇 */}
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center'
                    }}
                  >
                    <span
                      style={{
                        minWidth: '44px',
                        flexShrink: 0,
                        fontSize: '14px',
                        color: '#666'
                      }}
                    >
                      結束：
                    </span>
                    <Select
                      placeholder='年'
                      value={dateRange?.[1]?.year()}
                      onChange={(year) => {
                        const currentEnd = dateRange?.[1] || tzNow()
                        const newEnd = currentEnd.year(year)
                        if (dateRange?.[0]) {
                          setDateRange([dateRange[0], newEnd])
                        } else {
                          const startDate = newEnd.subtract(1, 'month')
                          setDateRange([startDate, newEnd])
                        }
                      }}
                      style={{ flex: '1.2 1 0', minWidth: 92 }}
                    >
                      {Array.from(
                        { length: 5 },
                        (_, i) => tzNow().year() - 2 + i
                      ).map((year) => (
                        <Option key={year} value={year}>
                          {year}年
                        </Option>
                      ))}
                    </Select>
                    <Select
                      placeholder='月'
                      value={
                        dateRange?.[1] ? dateRange[1].month() + 1 : undefined
                      }
                      onChange={(month) => {
                        const currentEnd = dateRange?.[1] || tzNow()
                        const newEnd = currentEnd.month(month - 1)
                        if (dateRange?.[0]) {
                          setDateRange([dateRange[0], newEnd])
                        } else {
                          const startDate = newEnd.subtract(1, 'month')
                          setDateRange([startDate, newEnd])
                        }
                      }}
                      style={{ flex: '1 1 0', minWidth: 84 }}
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(
                        (month) => (
                          <Option key={month} value={month}>
                            {month}月
                          </Option>
                        )
                      )}
                    </Select>
                    <Select
                      placeholder='日'
                      value={dateRange?.[1]?.date()}
                      onChange={(day) => {
                        const currentEnd = dateRange?.[1] || tzNow()
                        const newEnd = currentEnd.date(day)
                        if (dateRange?.[0]) {
                          setDateRange([dateRange[0], newEnd])
                        } else {
                          const startDate = newEnd.subtract(7, 'day')
                          setDateRange([startDate, newEnd])
                        }
                      }}
                      style={{ flex: '1 1 0', minWidth: 84 }}
                    >
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(
                        (day) => (
                          <Option key={day} value={day}>
                            {day}日
                          </Option>
                        )
                      )}
                    </Select>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  marginTop: '24px',
                  paddingTop: '20px',
                  borderTop: '1px solid #e5e5e7'
                }}
              >
                <Button onClick={handleReset} style={{ flex: 1 }}>
                  重置
                </Button>
                <Button
                  type='primary'
                  onClick={() => {
                    handleSearchSubmit()
                    setAdvancedSearchVisible(false)
                  }}
                  loading={searchLoading}
                  style={{ flex: 1 }}
                >
                  搜索
                </Button>
              </div>
            </div>
          </AdvancedSearchDrawer>
        </>
      )}

      <ResponsiveTable>
        {loading ? (
          <div className='empty-container'>
            <Spin size='large'>
              <div style={{ padding: '50px', textAlign: 'center' }}>
                載入中...
              </div>
            </Spin>
          </div>
        ) : (
          renderTableContent()
        )}
      </ResponsiveTable>

      <Drawer
        title={<Title level={4}>新增預約</Title>}
        placement='right'
        onClose={closeAddDrawer}
        open={isAddDrawerVisible}
        width={420}
        extra={
          <Space>
            <Button onClick={closeAddDrawer}>取消</Button>
            <Button type='primary' onClick={() => form.submit()}>
              確認新增
            </Button>
          </Space>
        }
      >
        {isLoadingData ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin tip='載入中...' />
          </div>
        ) : (
          <Form
            form={form}
            layout='vertical'
            onFinish={handleAddBooking}
            initialValues={{
              customerType: 'new',
              customerSource: 'none',
              notifyCustomer: true,
              // 同 Appointments.js：後台建立的預約預設不收定金
              depositStatus: 'not_required',
              ...(isCustomerView && customerInfo
                ? (() => {
                    const d = getCustomerDisplayData(customerInfo)
                    const pu = resolvePlatformUser(customerInfo)
                    return {
                      userName:
                        (d.name && d.name !== '未知' && d.name) || pu?.name,
                      phone:
                        (d.phone && d.phone !== '未提供' && d.phone) ||
                        pu?.phone,
                      email:
                        (d.email && d.email !== '未知' && d.email) || pu?.email,
                      customerId: customerInfo._id
                    }
                  })()
                : {})
            }}
          >
            {!isCustomerView && (
              <div
                className='customer-search-container'
                style={{ position: 'relative' }}
              >
                <Form.Item
                  name='userName'
                  label='客戶姓名'
                  rules={[{ required: true, message: '請輸入客戶姓名' }]}
                >
                  <div style={{ position: 'relative' }}>
                    <Input
                      value={customerNameValue}
                      onChange={(e) => {
                        const value = e.target.value
                        setCustomerNameValue(value)
                        form.setFieldsValue({ userName: value })
                        // 只有在輸入法組合結束後才觸發搜尋
                        if (!isComposing) {
                          searchCustomers(value)
                        }
                      }}
                      onCompositionStart={() => {
                        setIsComposing(true)
                      }}
                      onCompositionEnd={(e) => {
                        setIsComposing(false)
                        // 輸入法組合結束後觸發搜尋
                        const value = e.target.value
                        searchCustomers(value)
                      }}
                      placeholder='輸入姓名搜索客戶'
                    />
                    {/* 搜索結果框 - 在姓名輸入框正下方 */}
                    {showCustomerList && customers.length > 0 && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          background: '#fff',
                          border: '1px solid #d9d9d9',
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          maxHeight: '200px',
                          overflowY: 'auto'
                        }}
                      >
                        {customers.map((customer) => {
                          const pu = resolvePlatformUser(customer)
                          return (
                            <div
                              key={customer._id}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f0f0f0'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#f5f5f5'
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = '#fff'
                              }}
                              onClick={() => selectCustomer(customer)}
                            >
                              <div style={{ fontWeight: 500, color: '#1d1d1f' }}>
                                {(() => {
                                  const dn =
                                    getCustomerDisplayData(customer).name
                                  return dn && dn !== '未知'
                                    ? dn
                                    : customer.userName ||
                                        pu?.name ||
                                        '未命名客戶'
                                })()}
                              </div>
                              <div
                                style={{ fontSize: '12px', color: '#6c757d' }}
                              >
                                {(() => {
                                  const businessRelation =
                                    customer.businessRelations?.[0]
                                  const businessData =
                                    businessRelation?.businessSpecificData ||
                                    customer.businessSpecificData ||
                                    {}
                                  return (
                                    businessData.phone ||
                                    pu?.phone ||
                                    customer.phone ||
                                    ''
                                  )
                                })()}{' '}
                                {customer.email || pu?.email
                                  ? `• ${customer.email || pu?.email}`
                                  : ''}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </Form.Item>

                <Form.Item
                  name='customerType'
                  label='客戶類型'
                  rules={[{ required: true, message: '請選擇客戶類型' }]}
                >
                  <Select>
                    <Select.Option value='new'>新客戶</Select.Option>
                    <Select.Option value='existing'>舊客戶</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  name='customerSource'
                  label='客戶來源'
                  rules={[{ required: true, message: '請選擇客戶來源' }]}
                >
                  <Select>
                    <Select.Option value='none'>無</Select.Option>
                    <Select.Option value='online'>網路</Select.Option>
                    <Select.Option value='referral'>轉介</Select.Option>
                    <Select.Option value='walk-in'>路過</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item name='phone' label='聯絡電話'>
                  <Input
                    onChange={(e) =>
                      form.setFieldsValue({ phone: e.target.value })
                    }
                    placeholder='請輸入聯絡電話'
                  />
                </Form.Item>

                <Form.Item
                  name='email'
                  label='電子郵件'
                  rules={[
                    { type: 'email', message: '請輸入有效的電子郵件地址' }
                  ]}
                >
                  <Input />
                </Form.Item>
              </div>
            )}

            <Form.Item
              name='productIds'
              label='選擇服務'
              rules={[
                { required: true, message: '請選擇至少一項服務' },
                () => ({
                  validator(_, value) {
                    if (!value || value.length === 0) {
                      return Promise.reject(new Error('請選擇至少一項服務'))
                    }
                    return Promise.resolve()
                  }
                })
              ]}
            >
              <Select
                mode='multiple'
                showSearch
                placeholder='請選擇服務（可輸入關鍵字搜索）'
                optionFilterProp='label'
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={handleServicesSelect}
                style={{ width: '100%' }}
              >
                {products.map((product) => (
                  <Select.Option
                    key={product._id}
                    value={product._id}
                    label={`${product.name} - $${product.price} (${product.duration}分鐘)`}
                  >
                    {product.name} - ${product.price} ({product.duration}分鐘)
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {/* 顯示已選服務摘要與價格編輯 */}
            {selectedServices.length > 0 && (
              <div
                style={{
                  marginTop: 8,
                  padding: 12,
                  background: '#f5f5f5',
                  borderRadius: 6,
                  fontSize: 13,
                  marginBottom: 24
                }}
              >
                <div style={{ marginBottom: 12, fontWeight: 600 }}>
                  已選服務 ({selectedServices.length} 項)：
                </div>
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                >
                  {selectedServices.map((service) => {
                    const serviceId = service._id
                    const basePrice = service.price || 0
                    const currentPrice =
                      servicePrices[serviceId]?.actualPrice ?? basePrice
                    const priceInfo = servicePrices[serviceId] || {
                      basePrice,
                      actualPrice: basePrice
                    }

                    return (
                      <div
                        key={service._id}
                        style={{
                          padding: 12,
                          background: '#fff',
                          borderRadius: 6,
                          border: '1px solid #d9d9d9'
                        }}
                      >
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>
                          {service.name} ({service.duration}分鐘)
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            flexWrap: 'wrap'
                          }}
                        >
                          <span style={{ fontSize: 12, color: '#6c757d' }}>
                            原價: ${basePrice.toLocaleString()}
                          </span>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8
                            }}
                          >
                            <span style={{ fontSize: 12, color: '#6c757d' }}>
                              數量:
                            </span>
                            <InputNumber
                              min={1}
                              value={serviceQuantities[serviceId] || 1}
                              onChange={(value) => {
                                const quantity = value || 1
                                setServiceQuantities((prev) => ({
                                  ...prev,
                                  [serviceId]: quantity
                                }))

                                // 重新計算總價（考慮數量）
                                const updatedPrices = { ...servicePrices }
                                const updatedQuantities = {
                                  ...serviceQuantities,
                                  [serviceId]: quantity
                                }
                                const totalBasePrice = selectedServices.reduce(
                                  (sum, s) => {
                                    const sBasePrice = s.price || 0
                                    const sQuantity =
                                      updatedQuantities[s._id] || 1
                                    return sum + sBasePrice * sQuantity
                                  },
                                  0
                                )
                                const totalActualPrice =
                                  selectedServices.reduce((sum, s) => {
                                    const sPrice =
                                      updatedPrices[s._id]?.actualPrice ??
                                      s.price ??
                                      0
                                    const sQuantity =
                                      updatedQuantities[s._id] || 1
                                    return sum + sPrice * sQuantity
                                  }, 0)
                                form.setFieldsValue({
                                  originalPrice: totalBasePrice,
                                  discountedPrice: totalActualPrice
                                })
                              }}
                              style={{
                                width: 80,
                                textAlign: 'left'
                              }}
                              size='small'
                            />
                          </div>
                          <InputNumber
                            min={0}
                            value={currentPrice}
                            onChange={(value) => {
                              const updatedPrices = {
                                ...servicePrices,
                                [serviceId]: {
                                  basePrice: priceInfo.basePrice || basePrice,
                                  actualPrice: value || basePrice
                                }
                              }
                              setServicePrices(updatedPrices)

                              // 重新計算原價（basePrice 總和 * 數量）和優惠價（actualPrice 總和 * 數量）
                              const totalBasePrice = selectedServices.reduce(
                                (sum, s) => {
                                  const sBasePrice = s.price || 0
                                  const sQuantity =
                                    serviceQuantities[s._id] || 1
                                  return sum + sBasePrice * sQuantity
                                },
                                0
                              )
                              const totalActualPrice = selectedServices.reduce(
                                (sum, s) => {
                                  const sPrice =
                                    updatedPrices[s._id]?.actualPrice ??
                                    s.price ??
                                    0
                                  const sQuantity =
                                    serviceQuantities[s._id] || 1
                                  return sum + sPrice * sQuantity
                                },
                                0
                              )
                              form.setFieldsValue({
                                originalPrice: totalBasePrice,
                                discountedPrice: totalActualPrice
                              })
                            }}
                            style={{
                              width: 140,
                              textAlign: 'left'
                            }}
                            formatter={(value) =>
                              `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            }
                            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                            size='small'
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
                <div
                  style={{
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: '1px solid #d9d9d9',
                    color: '#666'
                  }}
                >
                  總價：$
                  {selectedServices
                    .reduce((sum, s) => {
                      const sPrice =
                        servicePrices[s._id]?.actualPrice ?? s.price ?? 0
                      const sQuantity = serviceQuantities[s._id] || 1
                      return sum + sPrice * sQuantity
                    }, 0)
                    .toLocaleString()}{' '}
                  | 總時長：{' '}
                  {selectedServices.reduce((sum, s) => {
                    const sQuantity = serviceQuantities[s._id] || 1
                    return sum + (s.duration || 0) * sQuantity
                  }, 0)}
                  分鐘
                </div>
              </div>
            )}

            <Form.Item
              name='participantCount'
              label='預約人數'
              rules={[
                { required: true, message: '請輸入預約人數' },
                { type: 'number', min: 1, message: '預約人數至少為1人' },
                { type: 'number', max: 10, message: '預約人數最多為10人' }
              ]}
              initialValue={1}
            >
              <InputNumber
                min={1}
                max={10}
                precision={0}
                style={{ width: '100%' }}
                placeholder='請輸入預約人數'
                onChange={(value) => {
                  setParticipantCount(value || 1)
                  form.setFieldsValue({ participantCount: value || 1 })
                }}
              />
            </Form.Item>

            <Form.Item
              name='stylistId'
              label='選擇造型師'
              rules={[{ required: true, message: '請先選擇造型師' }]}
              extra={
                !selectedStylistId
                  ? '請先選擇造型師，系統將根據員工和店家休假自動禁用不可預約的時間'
                  : ''
              }
            >
              <Select
                placeholder='請選擇造型師'
                onChange={handleStylistChange}
                value={selectedStylistId}
              >
                {stylists?.map((stylist) => (
                  <Select.Option key={stylist._id} value={stylist._id}>
                    {stylist.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name='date'
              label='預約日期和時間'
              rules={[
                { required: true, message: '請選擇預約日期和時間' },
                () => ({
                  validator(_, value) {
                    if (!selectedStylistId) {
                      return Promise.reject(new Error('請先選擇造型師'))
                    }
                    return Promise.resolve()
                  }
                })
              ]}
              extra={
                selectedStylistId
                  ? '系統已根據員工和店家休假自動禁用不可預約的時間'
                  : '請先選擇造型師'
              }
            >
              <div className='date-picker-wrapper'>
                <DesktopPicker>
                  <DatePicker
                    showTime={{ format: 'HH:mm', minuteStep: 15 }}
                    format='YYYY-MM-DD HH:mm'
                    style={{ width: '100%' }}
                    disabled={!selectedStylistId}
                    // 不禁用過去日期，僅透過 disabledDateTime 處理休假不可用時間
                    disabledDate={() => false}
                    disabledTime={disabledDateTime}
                    onChange={(date) => {
                      form.setFieldsValue({ date })
                      form.validateFields(['date'])
                    }}
                    placeholder={
                      selectedStylistId ? '選擇日期和時間' : '請先選擇造型師'
                    }
                  />
                </DesktopPicker>

                <MobilePicker>
                  <MobileDatePicker
                    visible={datePickerVisible}
                    onClose={() => setDatePickerVisible(false)}
                    precision='minute'
                    filter={{ minute: (val) => val % 15 === 0 }}
                    value={
                      form.getFieldValue('date')
                        ? form.getFieldValue('date').toDate()
                        : tzNow().toDate()
                    }
                    onConfirm={(val) => {
                      const newDate = dayjs(val)
                      // 檢查是否在不可用時間段
                      const dateStr = newDate.format('YYYY-MM-DD HH:mm')
                      if (
                        unavailableTimeSlots.some((slot) => slot === dateStr)
                      ) {
                        message.warning('選擇的時間不可預約，請選擇其他時間')
                        return
                      }
                      form.setFieldsValue({ date: newDate })
                      setDatePickerVisible(false)
                    }}
                    popupStyle={{ zIndex: 1500 }}
                    getContainer={null}
                    disabled={!selectedStylistId}
                  />
                  <div
                    onClick={() => {
                      if (!selectedStylistId) {
                        message.warning('請先選擇造型師')
                        return
                      }
                      setDatePickerVisible(true)
                    }}
                    style={{
                      cursor: selectedStylistId ? 'pointer' : 'not-allowed',
                      padding: '4px 11px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '2px',
                      backgroundColor: selectedStylistId ? '#fff' : '#f5f5f5',
                      opacity: selectedStylistId ? 1 : 0.6
                    }}
                  >
                    {form.getFieldValue('date')
                      ? dayjs(form.getFieldValue('date')).format(
                          'YYYY-MM-DD HH:mm'
                        )
                      : selectedStylistId
                        ? '選擇日期和時間'
                        : '請先選擇造型師'}
                  </div>
                </MobilePicker>
              </div>
            </Form.Item>

            {/* 店家沒啟用定金功能就別問了，值維持 initialValues 的 not_required */}
            {depositEnabled && (
              <Form.Item
                name='depositStatus'
                label='定金狀態'
                rules={[{ required: true, message: '請選擇定金狀態' }]}
              >
                <Select>
                  <Select.Option key='paid' value='paid'>
                    已付款
                  </Select.Option>
                  <Select.Option key='unpaid' value='unpaid'>
                    待付款
                  </Select.Option>
                  <Select.Option key='not_required' value='not_required'>
                    不需定金
                  </Select.Option>
                </Select>
              </Form.Item>
            )}

            <Form.Item name='note' label='備註'>
              <Input.TextArea rows={4} />
            </Form.Item>

            <Form.Item name='notifyCustomer' valuePropName='checked'>
              <Checkbox>通知客戶（LINE／Email）</Checkbox>
            </Form.Item>

            <Form.Item
              name='originalPrice'
              label='原價'
              rules={[{ required: true, message: '請輸入原價' }]}
            >
              <Input type='number' addonAfter='元' disabled />
            </Form.Item>

            <Form.Item
              name='discountedPrice'
              label='優惠價(結帳金額)'
              rules={[{ required: true, message: '請輸入優惠價' }]}
            >
              <Input type='number' addonAfter='元' />
            </Form.Item>
          </Form>
        )}
      </Drawer>

      {/* 價格預覽 Modal */}
      <Modal
        title='價格計算結果'
        open={showPricePreview}
        onCancel={() => setShowPricePreview(false)}
        footer={[
          <Button key='close' onClick={() => setShowPricePreview(false)}>
            關閉
          </Button>
        ]}
      >
        {priceCalculation && (
          <Space direction='vertical' style={{ width: '100%' }}>
            {priceCalculation.serviceDetails &&
              priceCalculation.serviceDetails.length > 0 && (
                <Card size='small' title='服務明細'>
                  {priceCalculation.serviceDetails.map((service, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Text>
                        {service.serviceName} ({service.duration} 分鐘)
                      </Text>
                      <Text strong>${service.price}</Text>
                    </div>
                  ))}
                </Card>
              )}

            {priceCalculation.additionalServicesDetails &&
              priceCalculation.additionalServicesDetails.length > 0 && (
                <Card size='small' title='加選服務明細'>
                  {priceCalculation.additionalServicesDetails.map(
                    (service, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Text>
                          + {service.serviceName} ({service.duration} 分鐘)
                        </Text>
                        <Text strong>${service.price}</Text>
                      </div>
                    )
                  )}
                </Card>
              )}

            <Card size='small'>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>原始總價:</Text>
                <Text>${priceCalculation.originalTotal}</Text>
              </div>
              {priceCalculation.discountAmount > 0 && (
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <Text>折扣金額:</Text>
                  <Text style={{ color: '#e74c3c' }}>
                    -${priceCalculation.discountAmount}
                  </Text>
                </div>
              )}
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text strong>最終價格:</Text>
                <Text strong style={{ color: '#1890ff', fontSize: 16 }}>
                  ${priceCalculation.finalPrice}
                </Text>
              </div>
            </Card>
          </Space>
        )}
      </Modal>

      {/* 改日期 Modal */}
      <Modal
        title='修改訂單日期'
        open={isUpdateDateModalVisible}
        onCancel={() => {
          setIsUpdateDateModalVisible(false)
          updateDateForm.resetFields()
          setSelectedBookingForDateUpdate(null)
        }}
        onOk={handleUpdateDateSubmit}
        okText='確認'
        cancelText='取消'
        width={isMobile ? '90%' : 500}
      >
        <Form
          form={updateDateForm}
          layout='vertical'
          initialValues={{
            date: null,
            startTime: null,
            endTime: null
          }}
        >
          <Form.Item
            name='date'
            label='日期'
            rules={[{ required: true, message: '請選擇日期' }]}
          >
            {isMobile ? (
              <>
                <MobileDatePicker
                  visible={updateDatePickerVisible}
                  onClose={() => setUpdateDatePickerVisible(false)}
                  precision='day'
                  value={updateDateForm.getFieldValue('date')?.toDate()}
                  onConfirm={(val) => {
                    updateDateForm.setFieldsValue({ date: dayjs(val) })
                    setUpdateDatePickerVisible(false)
                  }}
                />
                <div
                  onClick={() => setUpdateDatePickerVisible(true)}
                  style={{
                    cursor: 'pointer',
                    padding: '4px 11px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '2px',
                    backgroundColor: '#fff'
                  }}
                >
                  {updateDateForm.getFieldValue('date')
                    ? dayjs(updateDateForm.getFieldValue('date')).format(
                        'YYYY-MM-DD'
                      )
                    : '選擇日期'}
                </div>
              </>
            ) : (
              <DatePicker
                style={{ width: '100%' }}
                format='YYYY-MM-DD'
                placeholder='選擇日期'
              />
            )}
          </Form.Item>

          <Form.Item
            name='startTime'
            label='開始時間'
            rules={[
              { required: true, message: '請輸入開始時間' },
              {
                pattern: /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/,
                message: '時間格式必須為 HH:mm'
              }
            ]}
          >
            <Input placeholder='HH:mm (例如: 14:30)' />
          </Form.Item>

          <Form.Item
            name='endTime'
            label='結束時間（可選）'
            rules={[
              {
                pattern: /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/,
                message: '時間格式必須為 HH:mm'
              }
            ]}
          >
            <Input placeholder='HH:mm (例如: 16:30)' />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default BookingTable
